/* Wiring: sign in, load guests, print a run, work the scan desk. */

import * as api from './api.js';
import * as store from './store.js';
import { BY_SLUG, CARD_H, DETAIL_FIELDS, PANEL_H, PRINT_ORDER } from './layout.js';

// The white panel's share of the card's height, from the artboard itself.
const PANEL_RATIO = PANEL_H / CARD_H;
import {
  ATTENDANCE, ATTENDANCE_BY_SLUG, attendanceKnown, dateFields, hasQr, hasRsvpd,
  inDateWindow, issues, linkParents, printable, qrNote, RSVPD, summarise,
  toAttendee,
} from './model.js';
import { CameraScanner } from './camera.js';
import { decodeQr, importKey, looksLikeTicketQr } from './qrpayload.js';
import { downloadPdf, printPdf } from './printing.js';
import { groupByCategory, orderAttendees } from './ordering.js';
import {
  asPrinted, attachQrCodes, cardSizeMm, preload, qrPngDataUrl, renderBlanks,
  renderCards, renderLabels, undrawable,
} from './render.js';
import { renderPdfToCanvas } from './preview.js';
import { magicLink, rsvpBasis, rsvpLink } from './links.js';
import { registerServiceWorker } from './pwa.js';
import { deskStatus, shouldAutoRefresh } from './connection.js';

const $ = s => document.querySelector(s);
const $$ = s => [...document.querySelectorAll(s)];
const esc = s => String(s ?? '').replace(/[&<>"]/g,
  c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

const S = {
  attendees: [], loadedAt: '', status: 'CLAIMED',
  kind: 'cards', previewIdx: 0, pendingDup: null, blobUrls: [],
  runGroups: new Map(), duplicates: 0, missing: 0, glyphless: [],
  lastLabel: null, lastGuest: null, camera: null, payloads: {}, qrKey: null,
  lastScannedCode: '', serverDecrypt: false,
};

function toast(msg, ms = 2400) {
  const t = $('#toast');
  t.textContent = msg;
  t.classList.add('on');
  clearTimeout(t._x);
  t._x = setTimeout(() => t.classList.remove('on'), ms);
}

registerServiceWorker({
  onUpdateReady: apply => {
    $('#updateBar').classList.add('on');
    $('#updateBtn').onclick = apply;
  },
  onError: () => toast('Offline mode is unavailable this time — the app still works online.', 6000),
});

/* ============================== sign in ============================== */

$('#loginForm').addEventListener('submit', async e => {
  e.preventDefault();
  const btn = $('#loginBtn');
  btn.disabled = true;
  btn.textContent = 'Signing in…';
  $('#loginError').textContent = '';
  try {
    await api.login($('#email').value.trim(), $('#password').value);
    $('#password').value = '';
    await start();
  } catch (err) {
    $('#loginError').textContent = err.message;
  } finally {
    btn.disabled = false;
    btn.textContent = 'Sign in';
  }
});

$('#signOut').onclick = async () => {
  api.clearToken();
  await store.clearRoster();
  await store.clearPayloadIndex();
  location.reload();
};

/* The session is silent until it is nearly over, then says so. Expiry no longer
   throws the desk to the sign-in screen: the roster stays usable offline, and
   the status line says what is possible. */
let watching = false;
function watchSession() {
  if (watching) return;
  watching = true;
  const tick = () => {
    const left = api.sessionRemaining();
    $('#signOut').textContent = left > 0 && left < 30 * 60
      ? `sign out · ${Math.ceil(left / 60)} min left` : 'sign out';
    renderConn();
  };
  tick();
  setInterval(tick, 60000);
}

const C = { online: navigator.onLine };

/* Reaching this app's own host is the cheapest honest test. It does not prove the
   ticket API is up; a roster refresh does that. Any HTTP answer counts as online. */
async function probe() {
  if (!navigator.onLine) return false;
  try {
    await fetch('/api/resolve', { cache: 'no-store', signal: AbortSignal.timeout(5000) });
    return true;
  } catch {
    return false;
  }
}

function renderConn() {
  const status = deskStatus({ online: C.online, sessionValid: api.sessionValid() });
  const el = $('#connState');
  const tone = { online: 'ok', offline: 'warn', expired: 'warn', 'offline-expired': 'bad' }[status.state];
  el.className = `tag ${tone}`;
  el.textContent = status.state.startsWith('offline')
    ? `${status.label} · roster ${describeAge(S.loadedAt)}` : status.label;
  $('#connAction').classList.toggle('hide', !status.needsSignIn);
  $('#loadBtn').disabled = !status.canRefresh;
}

async function checkConnection() {
  C.online = await probe();
  renderConn();
  const busy = $('#loadBtn').textContent === 'Loading…';
  if (!busy && shouldAutoRefresh({
    online: C.online, sessionValid: api.sessionValid(), loadedAt: S.loadedAt,
  })) loadGuests();
}

addEventListener('online', checkConnection);
addEventListener('offline', () => { C.online = false; renderConn(); });
setInterval(checkConnection, 60000);
$('#connAction').onclick = () => requireSignIn(new Error('Sign in to refresh the guest list.'));

function requireSignIn(err) {
  api.clearToken();
  $('#app').classList.add('hide');
  $('#login').classList.remove('hide');
  $('#loginError').textContent = err?.message || '';
}

/* ============================== routing ============================== */

function go(view) {
  $$('.view').forEach(v => v.classList.toggle('on', v.dataset.view === view));
  $$('nav a').forEach(a => a.classList.toggle('on', a.dataset.v === view));
  if (location.hash !== '#' + view) location.hash = view;
  if (view === 'scan') {
    refreshFeed();
    setTimeout(() => $('#deskInput').focus(), 60);
  } else {
    stopCamera();
  }
  if (view === 'print') refreshPreview();
}
$$('nav a').forEach(a => a.onclick = e => { e.preventDefault(); go(a.dataset.v); });
addEventListener('hashchange', () => go(location.hash.slice(1) || 'guests'));

/* ========================== preferences form ========================= */

const P = store.prefs();

/* `kind` says how to read the control back. A select holding "true"/"false"
   needs 'bool': taken as a plain value its answer is the *string* "false",
   which is truthy, so the setting silently did the opposite of what it said. */
function bindPref(sel, key, kind = 'value') {
  const el = $(sel);
  if (kind === 'checked') el.checked = P[key];
  else el.value = kind === 'bool' ? String(P[key]) : P[key];
  el.addEventListener('change', () => {
    P[key] = kind === 'checked' ? el.checked
      : kind === 'bool' ? el.value === 'true'
      : (typeof P[key] === 'number' ? Number(el.value) : el.value);
    store.savePrefs({ [key]: P[key] });
    if (sel.startsWith('#label') || sel === '#pageMode' || sel === '#cardH'
        || sel === '#splitByCategory' || sel === '#familyMode') refreshPreview();
  });
}
bindPref('#statusFilter', 'status');
$('#statusFilter').addEventListener('change', () => loadGuests());
bindPref('#splitByCategory', 'splitByCategory', 'bool');
bindPref('#familyMode', 'familyMode');
bindPref('#pageMode', 'pageMode');
bindPref('#cardH', 'cardHeightMm');

/* Only the height is asked for; say what width that makes, so nobody has to
   work out 95 x h / 130 at the printer. */
const mm = n => (Math.round(n * 10) / 10).toString();

/* Every place that names a size reads it from the settings rather than
   carrying its own copy. Three copies of "95 x 130" survived the card shrinking
   to 109 mm, and a button that names the wrong size is a reason to set the
   printer wrong. */
function showSizes() {
  const card = cardSizeMm(P.cardHeightMm);
  const panelH = card.heightMm * PANEL_RATIO;
  $('#cardSizeNote').textContent =
    `${mm(card.widthMm)} × ${mm(card.heightMm)} mm — the artwork's proportions, `
    + `${Math.round(card.heightMm / 1.3)}% of the 95 × 130 artboard.`;
  // The modal can override the label size for one print, so read its fields.
  const lw = Number($('#pmW').value) || P.labelWidthMm;
  const lh = Number($('#pmH').value) || P.labelHeightMm;
  $('#pmKind [data-kind=label]').textContent = `Label ${mm(lw)} × ${mm(lh)}`;
  $('#pmKind [data-kind=card]').textContent =
    `Card ${mm(card.widthMm)} × ${mm(card.heightMm)}`;
  $('#panelNote').innerHTML = `The card's white panel is
    <b>${mm(card.widthMm)} × ${mm(panelH)} mm</b> at the current card size. Set
    the printer's paper to the label size and choose 100% / actual size — the
    page is sized in the document, so nothing gets rescaled.`;
}
showSizes();
for (const id of ['#cardH', '#labelW', '#labelH', '#deskLabelW', '#deskLabelH',
                 '#pmW', '#pmH']) {
  $(id).addEventListener('change', showSizes);
}
bindPref('#cropMarks', 'cropMarks', 'checked');
bindPref('#familyOnly', 'familyOnly', 'checked');
bindPref('#labelW', 'labelWidthMm');
bindPref('#labelH', 'labelHeightMm');
bindPref('#labelM', 'labelMarginMm');
bindPref('#labelGuides', 'labelGuides', 'checked');
// The desk and the batch screen edit the same label size; keep them in step.
for (const [desk, batch, key] of [['#deskLabelW', '#labelW', 'labelWidthMm'],
                                  ['#deskLabelH', '#labelH', 'labelHeightMm']]) {
  $(desk).value = P[key];
  $(desk).addEventListener('change', () => {
    P[key] = Number($(desk).value) || P[key];
    $(batch).value = P[key];
    store.savePrefs({ [key]: P[key] });
  });
  $(batch).addEventListener('change', () => { $(desk).value = $(batch).value; });
}

/* =========================== badge fields ============================ */

function renderFieldPicker() {
  $('#fieldPicker').innerHTML = DETAIL_FIELDS.map(f => `
    <button data-field="${f.key}" aria-pressed="${P.fields.includes(f.key)}">
      ${f.label}</button>`).join('');
  $$('#fieldPicker button').forEach(b => b.onclick = () => {
    const on = b.getAttribute('aria-pressed') !== 'true';
    b.setAttribute('aria-pressed', on);
    // Keep the declared order rather than click order: the badge reads the
    // same whichever way they were switched on.
    P.fields = DETAIL_FIELDS.map(f => f.key).filter(k =>
      $(`#fieldPicker [data-field="${k}"]`).getAttribute('aria-pressed') === 'true');
    store.savePrefs({ fields: P.fields });
    refreshPreview();
  });
}

/* ============================== guests =============================== */

/* A cached roster is only as true as the moment it was pulled, and tickets do
   get cancelled and reissued between one day and the next. "loaded 10:55" looks
   identical whether that was ten minutes or three days ago, which is how a link
   came to be generated for a ticket that no longer existed. Say the age in
   words, and say so loudly once it stops being trustworthy. */
const STALE_AFTER_HOURS = 4;

function ageInHours(iso) {
  const then = Date.parse(iso);
  return Number.isFinite(then) ? (Date.now() - then) / 3600000 : Infinity;
}

function describeAge(iso) {
  const hours = ageInHours(iso);
  if (!Number.isFinite(hours)) return 'at an unknown time';
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} min ago`;
  if (hours < 24) return `${Math.round(hours)} h ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

function showStaleness(iso) {
  const hours = ageInHours(iso);
  const el = $('#staleWarn');
  if (hours < STALE_AFTER_HOURS) return el.classList.add('hide');
  el.classList.remove('hide');
  el.innerHTML = `This list was loaded <b>${esc(describeAge(iso))}</b>. Tickets
    get cancelled and reissued — reload before printing or sending links.`;
}

/* Everything worth typing into a search box. Email is in here because it is
   what someone reaches for when a name is spelled three different ways. */
const haystack = a => `${a.name} ${a.company} ${a.pillar} ${a.ticket} ${a.email}`
  .toLowerCase();

function catChip(slug, count) {
  return `<button class="cat" data-cat="${slug}" aria-pressed="false"
    style="--c:var(--${slug})"><i></i>${BY_SLUG[slug].label}
    <b>${count.toLocaleString()}</b></button>`;
}

/* ------------------------------ RSVP filter --------------------------

   The Guests tab starts with nothing pressed, meaning "show me everyone", so
   the list can be looked through. The print run starts pressed on the answers
   that print, because printing a badge for someone who never replied is the
   mistake this is here to prevent. */

const attCounts = list => {
  const counts = {};
  for (const a of list) counts[a.attendance] = (counts[a.attendance] || 0) + 1;
  return counts;
};

function attChips(el, counts, pressed) {
  el.innerHTML = ATTENDANCE.filter(x => counts[x.slug]).map(x => `
    <button class="cat" data-att="${x.slug}"
      aria-pressed="${pressed.includes(x.slug)}" style="--c:${x.colour}"><i></i>
      ${esc(x.label)} <b>${counts[x.slug].toLocaleString()}</b></button>`).join('');
}

const chosenAtt = sel => $$(`${sel} .cat[aria-pressed=true]`).map(b => b.dataset.att);

/* An empty selection means no filter, not an empty list -- on the Guests tab
   that is "everyone", and on a print run it is caught before anything is
   generated rather than silently producing nothing. */
const passesAtt = (a, chosen) => !chosen.length || chosen.includes(a.attendance);

/* The raw values behind anything that could not be placed, so a wording change
   on the ticketing side shows up here instead of quietly dropping guests. */
function unrecognisedAttendance(list) {
  const seen = new Map();
  for (const a of list) {
    if (a.attendance !== 'other') continue;
    seen.set(a.attendanceRaw, (seen.get(a.attendanceRaw) || 0) + 1);
  }
  return [...seen].map(([raw, n]) => `${raw} (${n})`);
}

function renderGuests() {
  const list = S.attendees;
  const good = list.filter(printable);
  const allCounts = summarise(list);
  const loaded = list.length > 0;

  $('#statusDot').classList.toggle('live', loaded);
  $('#statusText').textContent = loaded
    ? `${good.length.toLocaleString()} printable` : 'not loaded';
  $('#guestsBody').classList.toggle('hide', !loaded);
  $('#loadState').classList.toggle('hide', loaded);
  if (!loaded) return;

  const stage = api.STAGES.find(x => x.param === S.status);
  $('#guestsTitle').textContent = good.length === list.length
    ? `${good.length.toLocaleString()} guests`
    : `${good.length.toLocaleString()} printable`;
  $('#guestsSub').textContent = [
    `SMDD 2026`,
    stage ? stage.label.toLowerCase() : 'every stage',
    `${list.length.toLocaleString()} ticket${list.length === 1 ? '' : 's'}`,
    `loaded ${describeAge(S.loadedAt)}`,
  ].join(' · ');
  showStaleness(S.loadedAt);

  renderGuestCats();
  /* Family is not a category of its own here -- as a chip it read as "all
     family", which is how a run came to carry family whose host was never in
     it. Each category carries its own instead. */
  const kin = {};
  for (const a of list) {
    if (a.category !== 'family' || !a.parent) continue;
    const host = S.attendees.find(x => x.ticket === a.parent);
    if (host) kin[host.category] = (kin[host.category] || 0) + 1;
  }
  if (!Array.isArray(P.familyByCat)) {
    P.familyByCat = Object.keys(kin);          // first run: all of them on
    store.savePrefs({ familyByCat: P.familyByCat });
  }
  $('#printCats').innerHTML = PRINT_ORDER
    .filter(c => allCounts[c] && c !== 'family')
    .map(c => `<span class="catwrap" style="--c:var(--${c});--c-ink:var(--${c}-ink)"
      >${catChip(c, allCounts[c])}${kin[c] ? `
      <button class="kin" data-kin="${c}"
        aria-pressed="${P.familyByCat.includes(c)}"
        >+ ${kin[c].toLocaleString()} family</button>` : ''}</span>`).join('');

  $$('#printCats .kin').forEach(b => b.onclick = () => {
    b.setAttribute('aria-pressed', b.getAttribute('aria-pressed') !== 'true');
    P.familyByCat = $$('#printCats .kin[aria-pressed=true]').map(x => x.dataset.kin);
    store.savePrefs({ familyByCat: P.familyByCat });
    S.previewIdx = 0;
    refreshPreview();
  });
  $$('#printCats .cat').forEach(b => b.onclick = () => {
    b.setAttribute('aria-pressed', b.getAttribute('aria-pressed') !== 'true');
    S.previewIdx = 0;
    refreshPreview();
  });
  store.setCategoryLabels(
    Object.fromEntries(PRINT_ORDER.map(c => [c, BY_SLUG[c].label])));
  renderDateFields(list);
  syncDateControls();
  fillGuestDateFields(list);
  const counts2 = attCounts(list);
  attChips($('#guestAtt'), counts2, []);
  attChips($('#printAtt'), counts2, P.printAttendance);
  $$('#guestAtt .cat').forEach(b => b.onclick = () => {
    b.setAttribute('aria-pressed', b.getAttribute('aria-pressed') !== 'true');
    renderGuestCats();
    runSearch();
  });
  $$('#printAtt .cat').forEach(b => b.onclick = () => {
    b.setAttribute('aria-pressed', b.getAttribute('aria-pressed') !== 'true');
    P.printAttendance = chosenAtt('#printAtt');
    store.savePrefs({ printAttendance: P.printAttendance });
    S.previewIdx = 0;
    refreshPreview();
  });

  /* Two ways this filter can be wrong, and both are quiet: the column may not
     be in the response at all, or its wording may have changed. Either would
     otherwise show up as a print run that is mysteriously short. */
  /* Rows lost in transit are not a filtering question and must not sit in the
     small print with one: every count on every screen is short by them. */
  $('#missingWarn').classList.toggle('hide', !S.missing);
  if (S.missing) {
    $('#missingWarn').innerHTML = `The ticket service says there are
      <b>${(S.attendees.length + S.missing).toLocaleString()}</b> tickets but
      sent <b>${S.attendees.length.toLocaleString()}</b> —
      <b>${S.missing.toLocaleString()}</b> never arrived, so every figure here is
      short by that many. Reload; if it persists, the list endpoint is dropping
      rows between pages and the ticketing team need to know.`;
  }

  const odd = unrecognisedAttendance(list);
  const warn = !attendanceKnown(list)
    ? `No RSVP information came back with these tickets — every guest reads as
       “no RSVP”. The filter is being ignored so nothing is wrongly held back.
       Ask the ticketing side which field carries it.`
    : odd.length
      ? `Not recognised as an RSVP answer: <b>${esc(odd.join(', '))}</b>.
         Those guests are in their own group above — select it if they should
         be printed.`
      : '';
  for (const id of ['#attWarn', '#printAttWarn']) {
    $(id).classList.toggle('hide', !warn);
    $(id).innerHTML = warn;
  }

  const bad = list.filter(a => !printable(a));
  const noQr = good.filter(a => !hasQr(a)).length;
  $('#guestHealth').className = 'tag ' + (bad.length || noQr ? 'warn' : 'ok');
  $('#guestHealth').textContent = !bad.length && !noQr
    ? 'every guest is printable'
    : [noQr && `${noQr.toLocaleString()} have no qr_string — those badges can't be scanned`,
       bad.length && `${bad.length} can't be printed`].filter(Boolean).join(' · ');
  const unknown = Object.entries(allCounts).filter(([k]) => k.startsWith('unmapped:'));
  $('#guestMeta').textContent = [
    unknown.length && 'unrecognised category: ' +
      unknown.map(([k, v]) => `${k.slice(9) || '(blank)'} (${v})`).join(', '),
    S.duplicates && `${S.duplicates.toLocaleString()} duplicate ticket${
      S.duplicates === 1 ? '' : 's'} ignored while loading`,
  ].filter(Boolean).join(' · ');

  $('#issuesBox').classList.toggle('hide', !bad.length);
  $('#issuesSummary').textContent = `Show the ${bad.length} skipped guests`;
  $('#issuesTable').innerHTML = `<table class="tbl">
    <tr><th>ticket</th><th>name</th><th>why</th></tr>
    ${bad.slice(0, 300).map(a => `<tr>
      <td class="mono">${esc(a.ticket) || '—'}</td><td>${esc(a.name) || '—'}</td>
      <td class="tag warn">${esc(issues(a).join(', '))}</td></tr>`).join('')}
    </table>`;

  $('#searchCat').innerHTML = '<option value="">All categories</option>' +
    PRINT_ORDER.filter(c => allCounts[c])
      .map(c => `<option value="${c}">${BY_SLUG[c].label}</option>`).join('');
  runSearch();
  refreshPreview();
}

/* The same field list the print run offers, on its own control. */
function fillGuestDateFields(list) {
  const fields = dateFields(list);
  const sel = $('#gDateField');
  const keep = sel.value;
  sel.innerHTML = fields.map(f => `<option value="${esc(f.field)}">${esc(f.field)} — ${
    f.filled === f.total ? 'all'
      : `${f.filled.toLocaleString()} of ${f.total.toLocaleString()}`}</option>`).join('')
    || '<option value="">no date fields</option>';
  if (fields.some(f => f.field === keep)) sel.value = keep;
  else if (fields.some(f => f.field === PREFERRED_DATE)) sel.value = PREFERRED_DATE;
  else if (fields.length) sel.value = fields[0].field;
  syncGuestDate();
}

function syncGuestDate() {
  const mode = $('#gDateMode').value;
  $('#gDateFrom').classList.toggle('hide', !mode || mode === 'before');
  $('#gDateTo').classList.toggle('hide', !mode || mode === 'after');

  const el = $('#gDateNote');
  const win = guestDateWindow();
  if (!mode) return el.textContent = '';
  if (!win) return el.textContent = 'Pick a day — nothing is filtered until you do.';
  const field = esc(win.field);
  const from = $('#gDateFrom').value, to = $('#gDateTo').value;
  el.innerHTML = `Counting tickets whose <b>${field}</b> falls `
    + (mode === 'before' ? `at or before <b>${esc(prettyDay(to))}</b>`
      : mode === 'after' ? `at or after <b>${esc(prettyDay(from))}</b>`
      : `between <b>${esc(prettyDay(from))}</b> and <b>${esc(prettyDay(to))}</b>`)
    + `. Tickets with no ${field} are not counted.`;
}

for (const id of ['#gDateField', '#gDateMode', '#gDateFrom', '#gDateTo']) {
  $(id).addEventListener('change', () => {
    syncGuestDate();
    renderGuestCats();
    runSearch();
  });
}

/* Category counts over whatever the RSVP and date filters leave standing, plus
   the totals spelled out. Counts that sat at the roster figure no matter what
   was filtered could not answer the question a report asks -- how many, of
   which category, under these conditions. */
function renderGuestCats() {
  const base = guestBase();
  const counts = summarise(base);
  $('#guestCats').innerHTML = PRINT_ORDER.filter(c => counts[c])
    .map(c => catChip(c, counts[c])).join('')
    || '<span class="hint">No guests match these filters.</span>';
  $$('#guestCats .cat').forEach(b => b.onclick = () => {
    $('#searchCat').value = b.dataset.cat;
    runSearch();
  });

  const total = S.attendees.length;
  const fam = base.filter(a => a.category === 'family').length;
  $('#guestTotals').innerHTML = base.length === total
    ? `<b>${total.toLocaleString()}</b> tickets in the roster.`
    : `<b>${base.length.toLocaleString()}</b> of ${total.toLocaleString()} tickets `
      + `match — ${(base.length - fam).toLocaleString()} guests and `
      + `${fam.toLocaleString()} family.`;
}

/* The Guests tab's own date window, kept apart from the print run's so a
   question about the roster ("how many claimed before the 21st") can be asked
   without disturbing a run that is being set up. */
function guestDateWindow() {
  const mode = $('#gDateMode').value;
  const field = $('#gDateField').value;
  if (!mode || !field) return null;
  const from = $('#gDateFrom').value;
  const to = $('#gDateTo').value;
  if (mode === 'before') return to ? { field, from: '', to: dayEnd(to) } : null;
  if (mode === 'after') return from ? { field, from: dayStart(from), to: '' } : null;
  return from || to ? { field, from: dayStart(from), to: dayEnd(to) } : null;
}

/* Everything the filters leave standing, before a category or a search term
   narrows it further. The category chips count over this, so their figures
   answer "of the guests matching the RSVP and date I chose, how many are VIP"
   rather than standing at the roster total whatever else is set. */
function guestBase() {
  const att = chosenAtt('#guestAtt');
  const win = guestDateWindow();
  return S.attendees.filter(a =>
    passesAtt(a, att)
    && (!win || inDateWindow(a, win.field, win.from, win.to)));
}

/* Everything the Guests tab is currently showing, before the table stops
   drawing at 80. The export walks the same list so a downloaded file cannot
   disagree with what was on screen. */
function filteredGuests() {
  const q = $('#search').value.trim().toLowerCase();
  const cat = $('#searchCat').value;
  return guestBase().filter(a =>
    (!cat || a.category === cat) && (!q || haystack(a).includes(q)));
}

function downloadCsv(text, basename) {
  const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, '');
  const url = URL.createObjectURL(
    new Blob([text], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `${basename}_${stamp}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 20000);
}

$('#exportGuests').onclick = () => {
  const rows = filteredGuests();
  if (!rows.length) return toast('Nothing to export in this view.', 3500);
  downloadCsv(store.attendeeCsv(rows), 'smdd2026_guests');
  toast(`Downloaded — ${rows.length.toLocaleString()} guest${
    rows.length === 1 ? '' : 's'}`, 4000);
};

let searchTimer;
function runSearch() {
  const printed = store.printedTickets();
  const rows = filteredGuests().slice(0, 80);
  $('#guestTable').innerHTML = `
    <tr><th>name</th><th>company</th><th>category</th><th>ticket</th>
      <th style="width:1%"></th></tr>
    ${rows.map(a => `<tr ${printable(a) ? `data-row="${esc(a.ticket)}"` : ''}>
      <td><b>${esc(a.name) || '—'}</b></td>
      <td>${esc(a.company) || '—'}</td>
      <td>${a.category
        ? `<span class="tag" style="background:color-mix(in srgb,var(--${a.category}) 13%,#fff);
            color:var(--${a.category}-ink)">${BY_SLUG[a.category].label}</span>`
        : `<span class="tag warn">${esc(issues(a).join(', '))}</span>`}
        ${printed.has(a.ticket) ? '<span class="tag ok">printed</span>' : ''}
        ${hasQr(a) ? '' : `<span class="tag warn">${esc(qrNote(a))}</span>`}</td>
      <td class="mono">${esc(a.ticket)}</td>
      <td><button class="sm" data-open="${esc(a.ticket)}"
        ${printable(a) ? '' : 'disabled'}>print…</button></td></tr>`).join('')}`;
  const open = ticket =>
    openPrintModal(S.attendees.find(x => x.ticket === ticket), 'label');
  $$('#guestTable [data-open]').forEach(b => b.onclick = e => {
    e.stopPropagation();
    open(b.dataset.open);
  });
  $$('#guestTable tr[data-row]').forEach(tr => tr.onclick = () =>
    open(tr.dataset.row));
}
$('#search').addEventListener('input', () => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(runSearch, 200);
});
$('#searchCat').addEventListener('change', runSearch);

/* Reload sits in the header, so it can be pressed from the scan desk or in the
   middle of setting up a run. The progress those tabs cannot see -- the bar
   lives on the Guests tab -- goes into the header line instead, which is on
   every screen. */
async function loadGuests() {
  const btn = $('#loadBtn');
  btn.disabled = true;
  btn.textContent = 'Loading…';
  $('#loadState').classList.remove('hide');
  $('#loadBar').classList.remove('hide');
  S.status = $('#statusFilter').value;
  try {
    const { rows, total, duplicates, missing } = await api.fetchTickets({
      status: S.status, sku: 'DAY12026',
      onProgress: (got, all) => {
        const text = `fetching… ${got.toLocaleString()} of ${all.toLocaleString()}`;
        $('#loadNote').textContent = text;
        $('#statusText').textContent = text;
        $('#loadBar').firstElementChild.style.width =
          `${all ? Math.round(100 * got / all) : 0}%`;
      },
    });
    S.attendees = linkParents(rows.map(toAttendee));
    S.loadedAt = new Date().toISOString();
    await store.saveRoster({
      attendees: S.attendees, loadedAt: S.loadedAt, status: S.status,
      sku: api.SKU,
    });
    S.payloads = await store.rememberPayloads(S.attendees);
    $('#loadBar').classList.add('hide');
    S.duplicates = duplicates;
    S.missing = missing;
    renderGuests();
    renderConn();
    toast(missing
      ? `Loaded ${S.attendees.length.toLocaleString()} tickets — but ${
          missing.toLocaleString()} the service counts never arrived`
      : `Loaded ${S.attendees.length.toLocaleString()} tickets`,
      missing ? 8000 : 2400);
    preload();
    checkGlyphs();
    useStoredKey();
  } catch (err) {
    if (err instanceof api.Unauthorised) return requireSignIn(err);
    $('#loadNote').textContent = err.message;
    $('#statusText').textContent = 'load failed';
    $('#loadBar').classList.add('hide');
    toast(err.message, 5000);          // the note is on a tab you may not be on
  } finally {
    btn.disabled = false;
    btn.textContent = 'Reload';
  }
}
$('#loadBtn').onclick = loadGuests;

/* ============================= print run ============================= */

$$('#kindSeg button').forEach(b => b.onclick = () => {
  S.kind = b.dataset.kind;
  $$('#kindSeg button').forEach(x =>
    x.setAttribute('aria-pressed', x === b));
  $$('.cards-only').forEach(e => e.classList.toggle('hide', S.kind !== 'cards'));
  $$('.labels-only').forEach(e => e.classList.toggle('hide', S.kind !== 'labels'));
  $('#runBtn').textContent = S.kind === 'labels'
    ? 'Generate label PDFs' : 'Generate PDFs';
  refreshPreview();
});

const selectedCats = () =>
  $$('#printCats .cat[aria-pressed=true]').map(b => b.dataset.cat);

/* Which categories bring their family, read off the chips themselves. */
const familyCats = () =>
  new Set($$('#printCats .kin[aria-pressed=true]').map(b => b.dataset.kin));

/* ------------------------------- waves -------------------------------

   A date field is offered rather than assumed, with its coverage shown, because
   the right one is a question about the ticketing side's data that cannot be
   answered from here: claimed_date is patchy and `updated_at` moves on any
   edit, not only on an RSVP. */

/* When a guest claimed their ticket is the closest thing the data has to when
   they replied, so it leads if it is there. It is also the patchiest, which is
   why the coverage is on every line and the gap is counted below the totals. */
const PREFERRED_DATE = 'claimed_date';

function renderDateFields(list) {
  const fields = dateFields(list);
  const sel = $('#dateField');
  const keep = sel.value;
  sel.innerHTML = fields.length
    ? fields.map(f => `<option value="${esc(f.field)}">${esc(f.field)} — ${
        f.filled === f.total ? 'all guests have it'
          : `${f.filled.toLocaleString()} of ${f.total.toLocaleString()} have it`
      }</option>`).join('')
    : '<option value="">no date fields in this data</option>';
  if (fields.some(f => f.field === keep)) sel.value = keep;
  else if (fields.some(f => f.field === PREFERRED_DATE)) sel.value = PREFERRED_DATE;
  else if (fields.length) sel.value = fields[0].field;
  showDateSample();
}

/* Whether a window on this field is a fixed batch at all.

   A date that is only ever set when a guest replies partitions them: a cut-off
   in the past can never gain anyone, because a later reply carries a later
   timestamp. A date set by the organiser beforehand -- confirmed_date,
   invited_date -- does not. It sits on tickets whose guest has not replied yet,
   so each of those replies drops into a batch whose cut-off passed weeks ago,
   and the same setting counts higher every day.

   The tell is in the data: does this field appear on tickets that have not
   RSVP'd? */
function warnUnstableField() {
  const field = $('#dateField').value;
  const el = $('#dateUnstable');
  const loose = field
    ? S.attendees.filter(a => a.dates?.[field] && !hasRsvpd(a)).length : 0;
  el.classList.toggle('hide', !loose || !$('#dateMode').value);
  if (!loose) return;
  const steady = dateFields(S.attendees)
    .map(f => f.field)
    .find(f => !S.attendees.some(a => a.dates?.[f] && !hasRsvpd(a)));
  el.innerHTML = `<b>${esc(field)}</b> is set before a guest replies —
    ${loose.toLocaleString()} ticket${loose === 1 ? '' : 's'} carry it without
    having RSVP'd yet. Each of those will drop into this batch when they reply,
    so the same cut-off counts higher every day and a printed batch cannot be
    reproduced.` + (steady
      ? ` Cut on <b>${esc(steady)}</b> instead — it exists only once a guest has
         replied, so a past cut-off never gains anyone.`
      : '');
}

/* Show one real value and how it was read. A timestamp with no timezone is
   taken as local time, and if the ticketing side means UTC every cut lands
   seven hours out -- invisible unless the two are put side by side. */
function showDateSample() {
  warnUnstableField();
  const field = $('#dateField').value;
  const el = $('#dateSample');
  const found = field && S.attendees.find(a => a.datesRaw?.[field]);
  if (!found) return el.textContent = '';
  const raw = found.datesRaw[field];
  const read = store.localStamp(found.dates[field]);
  el.innerHTML = `Read as local time: <b>${esc(raw)}</b> → <b>${esc(read)}</b>.` +
    (/\d{4}-\d{2}-\d{2}[ T]\d{2}:/.test(raw) && !/(Z|[+-]\d{2}:?\d{2})$/.test(raw)
      ? ' That value names no timezone — if the ticketing side stores UTC, every'
        + ' cut is 7 hours out. Worth checking one guest against their record.'
      : '');
}

/* The inputs are local wall-clock moments; the stored dates are UTC instants.
   A cut-off is given to the minute -- "Monday 23:59" -- so the end of a window
   runs to the last instant of that minute, otherwise a ticket claimed at
   23:59:30 falls the wrong side of its own cut-off.

   A bare date is still accepted, and means the start of that day. */
const localIso = (v, isEnd) => {
  if (!v) return '';
  const bare = v.length === 10;                       // a date with no time
  const d = new Date(bare ? `${v}T00:00:00` : `${v}:00`);
  if (Number.isNaN(+d)) return '';
  // A bare date used as the end of a window means all of that day; a moment
  // given to the minute means to the end of that minute.
  if (isEnd) { if (bare) d.setHours(23, 59, 59, 999); else d.setSeconds(59, 999); }
  return d.toISOString();
};
const dayStart = v => localIso(v, false);
const dayEnd = v => localIso(v, true);

function dateWindow() {
  const mode = $('#dateMode').value;
  if (!mode) return null;
  const field = $('#dateField').value;
  if (!field) return null;
  const from = $('#dateFrom').value;
  const to = $('#dateTo').value;
  if (mode === 'before') return to ? { field, from: '', to: dayEnd(to) } : null;
  if (mode === 'after') return from ? { field, from: dayStart(from), to: '' } : null;
  return from || to
    ? { field, from: dayStart(from), to: dayEnd(to) } : null;
}

function syncDateControls() {
  const mode = $('#dateMode').value;
  $('#dateRange').classList.toggle('hide', !mode);
  $('#dateFrom').closest('div').classList.toggle('hide', mode === 'before');
  $('#dateTo').closest('div').classList.toggle('hide', mode === 'after');
  $('#dateFromLabel').textContent = 'From';
  $('#dateToLabel').textContent = 'Up to and including';
  describeWindow();
}

const MOMENT = { day: 'numeric', month: 'short', year: 'numeric',
                hour: '2-digit', minute: '2-digit', hour12: false };
const prettyDay = v => {
  if (!v) return '';
  const d = new Date(v.length === 10 ? `${v}T00:00:00` : `${v}:00`);
  return Number.isNaN(+d) ? '' : d.toLocaleString(undefined, MOMENT);
};

/* "To (inclusive)" left the obvious question unanswered -- does the last day
   count, and up to what time? Rather than a better adjective, say the range
   back as the two instants it actually becomes. */
function describeWindow() {
  const el = $('#dateNote');
  const mode = $('#dateMode').value;
  if (!mode) return el.textContent = '';
  const field = $('#dateField').value || 'that date';
  const from = $('#dateFrom').value;
  const to = $('#dateTo').value;
  const need = mode === 'before' ? to : mode === 'after' ? from : (from && to);
  if (!need) {
    return el.textContent = 'Pick a day — nothing is filtered until you do.';
  }
  el.innerHTML = 'Includes everyone whose <b>' + esc(field) + '</b> falls '
    + (mode === 'before' ? `at or before <b>${esc(prettyDay(to))}</b>`
      : mode === 'after' ? `at or after <b>${esc(prettyDay(from))}</b>`
      : `between <b>${esc(prettyDay(from))}</b> and <b>${esc(prettyDay(to))}</b>`)
    + ', to the second.';

  /* A cut-off in the future is not a cut-off: the window is "everyone so far"
     and it grows with every claim, so the same setting counts differently
     tomorrow. That looks exactly like figures changing on their own, which is
     how it gets noticed -- after the batch has been printed. */
  const end = mode === 'after' ? '' : dayEnd($('#dateTo').value);
  $('#dateFuture').classList.toggle('hide', !(end && end > new Date().toISOString()));
  if (end && end > new Date().toISOString()) {
    $('#dateFuture').innerHTML = `That moment has not happened yet, so this is
      not a fixed batch — it is everyone who has claimed <b>so far</b>, and it
      will be larger tomorrow. For a batch you can reprint and reconcile, pick a
      cut-off in the past.`;
  }
}

for (const id of ['#dateField', '#dateMode', '#dateFrom', '#dateTo',
                 '#skipPrinted', '#familyOnly']) {
  $(id).addEventListener('change', () => {
    syncDateControls();
    showDateSample();
    S.previewIdx = 0;
    refreshPreview();
  });
}

/* ------------------------- reprint by ticket number ---------------------

   A list of tickets pasted from wherever the request came from -- a chat, a
   sheet, an email. Exactly those are printed. The category, RSVP, date and
   printed-log filters are deliberately not applied: a reprint is a request for
   a badge that already exists, and any of those filters would quietly drop
   someone who asked for one. */

const source = () => $('#sourceSeg [aria-pressed=true]')?.dataset.source || 'filters';

/* Every ticket number this event has issued is exactly sixteen letters and
   digits. Holding to that shape is what lets a whole row be pasted -- ticket,
   name, company -- without the name being read as tickets that do not exist. */
const TICKET_SHAPE = /^[A-Z0-9]{16}$/;

function parseTicketList() {
  // Anything that is not a ticket character separates tokens: newlines,
  // commas, spaces, tabs from a spreadsheet column, quotes from a CSV.
  const tokens = $('#ticketList').value.toUpperCase()
    .split(/[^A-Z0-9]+/).filter(Boolean);
  const raw = tokens.filter(t => TICKET_SHAPE.test(t));
  const ignored = tokens.length - raw.length;
  const seen = new Set();
  const dupes = [];
  const unique = [];
  for (const t of raw) {
    if (seen.has(t)) dupes.push(t); else { seen.add(t); unique.push(t); }
  }
  const byTicket = new Map(S.attendees.map(a => [a.ticket.toUpperCase(), a]));
  const found = [], missing = [], unprintable = [];
  for (const t of unique) {
    const a = byTicket.get(t);
    if (!a) missing.push(t);
    else if (!printable(a)) unprintable.push(a);
    else found.push(a);
  }
  return { raw, unique, dupes: [...new Set(dupes)], found, missing, unprintable,
           ignored };
}

/* What became of every line pasted. Nothing listed may vanish without a
   reason: a ticket that is not found is usually a typo or a ticket newer than
   the loaded list, and either way somebody is waiting for that badge. */
function renderTicketReport() {
  const r = parseTicketList();
  const el = $('#ticketReport');
  if (!r.raw.length) {
    return el.innerHTML = r.ignored
      ? `<p class="hint warnline" style="margin-top:10px">No ticket numbers in
          that — a ticket number is 16 letters and digits.</p>` : '';
  }
  const codes = list => `<span class="codes">${list.map(esc).join(', ')}</span>`;
  const out = [`<p class="hint" style="margin-top:10px"><b>${r.found.length}</b>
    of ${r.unique.length} ticket${r.unique.length === 1 ? '' : 's'} found and
    ready to print${r.raw.length !== r.unique.length
      ? ` (${r.raw.length} lines pasted)` : ''}.</p>`];
  if (r.ignored) {
    out.push(`<p class="hint" style="margin-top:8px">${r.ignored} other
      word${r.ignored === 1 ? '' : 's'} ignored — names, headings and the like.</p>`);
  }
  if (r.dupes.length) {
    out.push(`<p class="hint" style="margin-top:8px">Listed more than once,
      printed once: ${codes(r.dupes)}</p>`);
  }
  /* Not a reason to leave anyone out -- they were asked for by number -- but
     worth seeing, since a reprint for someone who never replied is sometimes a
     request for the wrong ticket. */
  const unanswered = r.found.filter(a => !hasRsvpd(a));
  if (unanswered.length) {
    out.push(`<p class="hint" style="margin-top:8px">${unanswered.length}
      ${unanswered.length === 1 ? 'has' : 'have'} not RSVP'd — printed anyway,
      since ${unanswered.length === 1 ? 'it was' : 'they were'} asked for.</p>`);
  }
  if (r.missing.length) {
    out.push(`<p class="hint warnline" style="margin-top:10px">Not in the loaded
      guest list (${r.missing.length}): ${codes(r.missing)}. Check for a typo —
      or press <b>Reload</b> if these were created since the list was loaded.</p>`);
  }
  if (r.unprintable.length) {
    out.push(`<p class="hint warnline" style="margin-top:10px">Found but cannot be
      printed (${r.unprintable.length}): ${r.unprintable.map(a =>
        `${esc(a.name || '(no name)')} <span class="codes">${esc(a.ticket)}</span>
         — ${esc(issues(a).join(', '))}`).join('; ')}</p>`);
  }
  el.innerHTML = out.join('');
}

/* A family badge whose host is not beside it in the run still belongs in the
   host's category's file, not a generic family one. */
function fileFamilyUnderHost(ordered) {
  if (P.familyMode !== 'with_parent') return ordered;
  const hostCategory = new Map(S.attendees
    .filter(a => a.category !== 'family').map(a => [a.ticket, a.category]));
  for (const a of ordered) {
    if (a.category !== 'family' || a.printGroup !== 'family') continue;
    const host = hostCategory.get(a.parent);
    if (host) a.printGroup = host;
  }
  return ordered;
}

$$('#sourceSeg button').forEach(b => b.onclick = () => {
  $$('#sourceSeg button').forEach(x => x.setAttribute('aria-pressed', x === b));
  const tickets = b.dataset.source === 'tickets';
  $$('.by-tickets').forEach(e => e.classList.toggle('hide', !tickets));
  $$('.by-filters').forEach(e => e.classList.toggle('hide', tickets));
  if (tickets) setTimeout(() => $('#ticketList').focus(), 0);
  S.previewIdx = 0;
  renderTicketReport();
  refreshPreview();
});

let ticketTimer;
$('#ticketList').addEventListener('input', () => {
  clearTimeout(ticketTimer);
  ticketTimer = setTimeout(() => {
    S.previewIdx = 0;
    renderTicketReport();
    refreshPreview();
  }, 250);
});

function selection() {
  const limit = Number($('#limit').value) || 0;
  if (source() === 'tickets') {
    const ordered = fileFamilyUnderHost(
      orderAttendees(parseTicketList().found, P.familyMode));
    return limit ? ordered.slice(0, limit) : ordered;
  }
  const cats = selectedCats();
  /* When the roster carries no RSVP information the filter is ignored rather
     than obeyed, which would print nothing at all. */
  const att = attendanceKnown(S.attendees) ? chosenAtt('#printAtt') : [];
  const win = dateWindow();
  const printed = $('#skipPrinted').checked ? store.printedTickets() : null;
  const eligible = S.attendees.filter(a =>
    passesAtt(a, att)
    && (!win || inDateWindow(a, win.field, win.from, win.to))
    && (!printed || !printed.has(a.ticket)));
  /* Hosts first: the categories picked above, family excluded -- a family badge
     belongs to whoever it is a guest of, and follows from that choice rather
     than being a category of its own. */
  const isFamily = a => a.category === 'family';
  const hosts = eligible.filter(a =>
    !isFamily(a) && (!cats.length || cats.includes(a.category)));

  /* Which category a family badge belongs to comes from its host, and that does
     not depend on the window -- but whether it prints in *this* run depends on
     its own claim, like everyone else's. Reading the host's category off the
     whole roster rather than off this run's hosts is what allows the ordinary
     case: the host claimed weeks ago and was printed then, the family claimed
     yesterday, and today's run is that family alone. */
  const wants = familyCats();
  const hostCategory = new Map(S.attendees
    .filter(a => !isFamily(a)).map(a => [a.ticket, a.category]));
  const family = eligible.filter(a => {
    if (!isFamily(a)) return false;
    const host = hostCategory.get(a.parent);
    if (!host) return false;                  // host not in the roster at all
    return (!cats.length || cats.includes(host)) && wants.has(host);
  });

  /* Hosts stay in the pool even for a family-only run so the ordering can still
     put each child behind its parent; they are dropped afterwards. */
  let ordered = orderAttendees([...hosts, ...family], P.familyMode);

  fileFamilyUnderHost(ordered);
  if ($('#familyOnly').checked) ordered = ordered.filter(isFamily);
  return limit ? ordered.slice(0, limit) : ordered;
}

/* Why each ticket in the roster is not in this run. Every ticket lands in
   exactly one bucket, in the order the run applies them, so the reasons and the
   run add back up to the roster -- which is the arithmetic a report has to be
   able to show. */
function exclusions(list) {
  const cats = selectedCats();
  const att = attendanceKnown(S.attendees) ? chosenAtt('#printAtt') : [];
  const win = dateWindow();
  const printed = $('#skipPrinted').checked ? store.printedTickets() : null;
  const wants = familyCats();
  const isFamily = a => a.category === 'family';
  const inRun = new Set(list.map(a => a.ticket));
  const hostCategory = new Map(S.attendees
    .filter(a => !isFamily(a) && (!cats.length || cats.includes(a.category)))
    .map(a => [a.ticket, a.category]));

  const why = new Map();
  const bump = k => why.set(k, (why.get(k) || 0) + 1);
  for (const a of S.attendees) {
    if (inRun.has(a.ticket)) continue;
    if (!printable(a)) bump("can't be printed");
    else if (!passesAtt(a, att)) bump('RSVP not in the chosen answers');
    else if (win && !a.dates?.[win.field]) bump(`no ${win.field}`);
    else if (win && !inDateWindow(a, win.field, win.from, win.to)) {
      bump('outside the date window');
    } else if (printed?.has(a.ticket)) bump('already printed here');
    else if (isFamily(a)) {
      bump(wants.has(hostCategory.get(a.parent))
        ? 'family whose host is not in this run' : 'family not asked for');
    } else if ($('#familyOnly').checked) bump('a host, in a family-only run');
    else bump('category not selected');
  }
  return why;
}

const labelOpts = () => ({
  widthMm: P.labelWidthMm, heightMm: P.labelHeightMm,
  marginMm: P.labelMarginMm, includeQr: P.labelIncludeQr,
  guides: P.labelGuides, fields: P.fields,
});

/* What this run covers, per category, before a single page is generated. A
   wave is decided on these numbers, and reading them off the finished PDF is
   too late to change anything. */
function renderBreakdown(list) {
  const byTickets = source() === 'tickets';
  const total = S.attendees.length;
  // Against a pasted list, "the rest of the roster" is not filtered out -- it
  // was never asked for. Only the list's own leftovers are worth reporting.
  const held = byTickets ? 0 : total - list.length;
  $('#runNote').textContent = list.length
    ? `${list.length.toLocaleString()} to print`
      + (held > 0 ? ` · ${held.toLocaleString()} filtered out` : '')
    : byTickets ? 'paste ticket numbers above' : 'nothing matches this selection';

  /* Count the way the run is actually cut, not by what each guest is. A family
     badge prints behind its host and lands in the host's file, so counting it
     under "family" made this table disagree with the files that came out --
     same total, different rows, and no way to tell which was lying. */
  const groups = groupByCategory(list);
  const family = list.filter(a => a.category === 'family');
  const onlyFamily = $('#familyOnly').checked;

  /* Guests and their family are counted in their own columns rather than one
     number with a caption. "VVIP incl. 22 family — 22" was the giveaway that a
     single column cannot say both things: in a family-only run every row is
     family, so the caption repeated the figure beside it. The row total is
     still what lands in that category's file. */
  const chip = c => `<span class="tag" style="background:color-mix(in srgb,
    var(--${c}) 13%,#fff);color:var(--${c}-ink)">${BY_SLUG[c].label}</span>`;
  const num = n => `<td class="mono">${n ? n.toLocaleString() : '—'}</td>`;

  const cells = PRINT_ORDER.filter(c => groups.has(c)).map(c => {
    const inGroup = groups.get(c);
    const kids = inGroup.filter(a => a.category === 'family').length;
    return { c, kids, guests: inGroup.length - kids, total: inGroup.length };
  });
  const anyFamily = cells.some(r => r.kids);

  const head = onlyFamily
    ? '<tr><th>family of</th><th>badges</th><th></th></tr>'
    : anyFamily
      ? '<tr><th>category</th><th>guests</th><th>family</th><th>total</th><th></th></tr>'
      : '<tr><th>category</th><th>badges</th><th></th></tr>';

  /* Each row can be taken away on its own, because the run is handed to
     whoever is cutting that category's cards, not to one person with all of
     them. The lists are kept as they were counted so the file and the figure
     beside it cannot drift apart. */
  S.runGroups = new Map([...groups, ['__all', list]]);
  const dl = (slug, n) => `<td><button class="sm ghost" data-dl="${slug}"
    title="Download these ${n.toLocaleString()} as CSV">CSV</button></td>`;

  const rows = cells.map(r => onlyFamily
    ? `<tr><td>${chip(r.c)}</td>${num(r.total)}${dl(r.c, r.total)}</tr>`
    : anyFamily
      ? `<tr><td>${chip(r.c)}</td>${num(r.guests)}${num(r.kids)}${num(r.total)}${
          dl(r.c, r.total)}</tr>`
      : `<tr><td>${chip(r.c)}</td>${num(r.total)}${dl(r.c, r.total)}</tr>`);

  const sum = (key) => cells.reduce((n, r) => n + r[key], 0);
  const all = `<td><button class="sm" data-dl="__all"
    title="Download the whole run as CSV">CSV</button></td>`;
  const totalRow = onlyFamily
    ? `<tr><th>total</th><th class="mono">${list.length.toLocaleString()}</th>${all}</tr>`
    : anyFamily
      ? `<tr><th>total</th><th class="mono">${sum('guests').toLocaleString()}</th>
         <th class="mono">${sum('kids').toLocaleString()}</th>
         <th class="mono">${list.length.toLocaleString()}</th>${all}</tr>`
      : `<tr><th>total</th><th class="mono">${list.length.toLocaleString()}</th>${all}</tr>`;

  /* A guest with no date in the chosen field cannot be placed in any wave. They
     are not swept into the current one -- that is how somebody gets printed
     twice -- so they are counted here instead of disappearing. */
  const win = byTickets ? null : dateWindow();
  const undated = win
    ? S.attendees.filter(a => passesAtt(a, attendanceKnown(S.attendees)
        ? chosenAtt('#printAtt') : []) && !a.dates?.[win.field]).length
    : 0;

  const n = family.length.toLocaleString();
  const plural = family.length === 1 ? '' : 's';
  $('#familyNote').textContent = !family.length
    ? 'No family in this run — press “+ family” on a category to bring theirs.'
    : onlyFamily
      ? `${n} family badge${plural}, grouped under the host they belong to.`
      : `${n} family badge${plural}, each behind its host.`;

  $('#runBreakdown').innerHTML = list.length ? `
    <table class="tbl">${head}${rows.join('')}${totalRow}</table>
    <p class="hint" style="margin-top:10px">${
      P.splitByCategory ? `${rows.length} file${rows.length === 1 ? '' : 's'}`
                        : 'one file'}${
      onlyFamily ? '' : `, named for the category` }</p>
    ${(() => {
      // Said again here because this is where the print button is.
      const lossy = new Set(S.glyphless.map(a => a.ticket));
      const n = list.filter(a => lossy.has(a.ticket)).length;
      return n ? `<p class="hint warnline" style="margin-top:10px">${n} badge${
        n === 1 ? '' : 's'} in this run ${n === 1 ? 'has' : 'have'} characters
        left off — Chinese, Japanese, Korean or emoji the font cannot print.
        The list is at the top of <b>Guests</b>.</p>` : '';
    })()}
    ${(() => {
      /* Overlap with what this device has already printed, stated whether or
         not it is being skipped -- it is the quickest answer to "have I done
         these already?". */
      if ($('#skipPrinted').checked || byTickets) return '';
      const done = store.printedTickets();
      const n = list.filter(a => done.has(a.ticket)).length;
      return n ? `<p class="hint" style="margin-top:6px">${n.toLocaleString()} of
        these were printed on this device before — tick <b>Skip guests already
        printed</b> to leave them out.</p>` : '';
    })()}
    ${held > 0 ? `<details style="margin-top:12px"><summary>${
      held.toLocaleString()} of ${total.toLocaleString()} tickets are not in this
      run</summary><table class="tbl">${[...exclusions(list)]
        .sort((x, y) => y[1] - x[1])
        .map(([reason, n]) => `<tr><td>${esc(reason)}</td>
          <td class="mono">${n.toLocaleString()}</td></tr>`).join('')}
      <tr><th>not in this run</th><th class="mono">${held.toLocaleString()}</th></tr>
      <tr><th>in this run</th><th class="mono">${list.length.toLocaleString()}</th></tr>
      <tr><th>all tickets</th><th class="mono">${total.toLocaleString()}</th></tr>
      </table></details>` : ''}

    ${undated ? `<p class="hint warnline" style="margin-top:12px">
      ${undated.toLocaleString()} RSVP'd guest${undated === 1 ? ' has' : 's have'}
      no <b>${esc(win.field)}</b> — no date means no wave, so they are not in
      this run or any other. Cut on a more complete field, or print them by
      turning the date filter off.</p>` : ''}`
    : `<p class="hint">${byTickets
        ? 'Nothing to print yet — paste ticket numbers above.'
        : 'Nothing matches this selection.'}</p>`;

  $$('#runBreakdown [data-dl]').forEach(b => b.onclick = () => {
    const slug = b.dataset.dl;
    const rows = S.runGroups.get(slug) || [];
    if (!rows.length) return toast('Nothing to download in that group.', 3500);
    const name = slug === '__all' ? 'all'
      : (onlyFamily ? `family-of-${slug}` : slug);
    downloadCsv(store.runCsv(rows), `smdd2026_run_${name}`);
    toast(`Downloaded — ${rows.length.toLocaleString()} ${
      rows.length === 1 ? 'badge' : 'badges'}`, 3500);
  });
}

let previewToken = 0;
async function refreshPreview() {
  if (!S.attendees.length) return;
  const list = selection();
  /* Say how many this run covers before it is started. The RSVP filter can cut
     a run down a long way, and finding that out from the finished PDF is too
     late. */
  renderBreakdown(list);
  if (!list.length) return;
  const a = list[S.previewIdx % list.length];
  const mine = ++previewToken;
  const one = await withQr(a);
  const bytes = S.kind === 'labels'
    ? await renderLabels([one], labelOpts())
    : await renderCards([one], { pageMode: 'single', fields: P.fields, cardHeightMm: P.cardHeightMm });
  if (mine !== previewToken) return;
  await renderPdfToCanvas(bytes, $('#previewCanvas'), { maxWidth: 620 });
  $('#prevWho').textContent = `${a.name} · ${a.company || '—'}`;
}
$('#prevPrev').onclick = () => {
  S.previewIdx = Math.max(0, S.previewIdx - 1);
  refreshPreview();
};
$('#prevNext').onclick = () => { S.previewIdx += 1; refreshPreview(); };

function offerDownload(bytes, filename, group, count) {
  const url = URL.createObjectURL(
    new Blob([bytes], { type: 'application/pdf' }));
  S.blobUrls.push(url);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.innerHTML = `<span><b>${esc(group)}</b> · ${count} ${
    S.kind === 'labels' ? 'labels' : 'cards'}</span>
    <span>${(bytes.length / 1e6).toFixed(1)} MB</span>`;
  $('#runFiles').append(a);
}

$('#runBtn').onclick = async () => {
  const list = selection();
  if (!list.length) return toast('Nothing printable in that selection');

  const btn = $('#runBtn');
  btn.disabled = true;
  $('#runResult').classList.add('hide');
  $('#runFiles').innerHTML = '';
  S.blobUrls.splice(0).forEach(URL.revokeObjectURL);
  $('#runBar').classList.remove('hide');
  const bar = $('#runBar').firstElementChild;
  const note = (text, done, total) => {
    $('#runNote').textContent = `${text} — ${done}/${total}`;
    bar.style.width = `${total ? Math.round(100 * done / total) : 0}%`;
  };

  try {
    await preload([...new Set(list.map(a => a.category))]);
    await attachQrCodes(list, (d, t) => note('drawing QR codes', d, t));

    const batches = P.splitByCategory
      ? groupByCategory(list) : new Map([['all-categories', list]]);
    const familyOnly = $('#familyOnly').checked;
    for (const [slug, cards] of batches) {
      const base = BY_SLUG[slug]?.label || slug;
      /* In a family-only run the group is still the host's category, so naming
         the file after it would claim it holds those guests. */
      const label = familyOnly && slug !== 'family' ? `family of ${base}` : base;
      const bytes = S.kind === 'labels'
        ? await renderLabels(cards, {
            ...labelOpts(),
            onProgress: (d, t) => note(`labels · ${label}`, d, t),
          })
        : await renderCards(cards, {
            pageMode: P.pageMode, showCropMarks: P.cropMarks, fields: P.fields,
            cardHeightMm: P.cardHeightMm,
            onProgress: (d, t) => note(`cards · ${label}`, d, t),
          });
      const kind = S.kind === 'labels' ? 'labels_' : '';
      // A reprint is filed apart from the batch it repeats, so the two are
      // never mistaken for each other in a downloads folder.
      const run = source() === 'tickets' ? 'reprint_' : '';
      offerDownload(bytes,
        `SMDD2026_${run}${kind}${slug}_${cards.length}.pdf`, label, cards.length);
    }

    $('#runBar').classList.add('hide');
    $('#runNote').textContent = 'done';
    $('#runResult').classList.remove('hide');
    const blank = list.filter(a => !hasQr(a)).length;
    $('#runSummary').innerHTML = `${list.length.toLocaleString()} ${
      S.kind === 'labels' ? 'labels' : 'cards'} across ${batches.size} file${
      batches.size > 1 ? 's' : ''}. Click to download.` + (blank
      ? ` <span class="tag warn">${blank.toLocaleString()} have no QR — those
          guests have not confirmed, so their badge can't be scanned</span>` : '');
    toast('PDFs ready');
  } catch (err) {
    $('#runBar').classList.add('hide');
    $('#runNote').innerHTML = `<span style="color:var(--bad)">${esc(err.message)}</span>`;
  } finally {
    btn.disabled = false;
  }
};

/* ========================= print options modal ======================= */

const M = { guest: null, kind: 'label', token: 0 };

const STAGE_NAME = { 1: 'draft', 2: 'approved', 3: 'invited', 4: 'confirmed' };

/** Everything about this guest that is worth seeing before printing them. */
function renderDetails(a) {
  const printed = store.printedTickets().get(a.ticket);
  const host = a.category === 'family'
    ? (a.parentName || (a.parent ? `${a.parent} — not in the loaded list` : '—'))
    : null;
  const rows = [
    ['Name', esc(a.name)],
    ['Company', esc(a.company) || '—'],
    ['Pillar', esc(a.pillar) || '—'],
    ['Position', esc(a.position) || '—'],
    ['Category', `<span class="tag" style="background:color-mix(in srgb,
       var(--${a.category}) 13%,#fff);color:var(--${a.category}-ink)"
       >${BY_SLUG[a.category].label}</span>`],
    host !== null && ['Host', esc(host)],
    ['Ticket', `<span class="mono">${esc(a.ticket)}</span>`],
    a.email && ['Email', esc(a.email)],
    a.phone && ['Phone', esc(a.phone)],
    ['Stage', `${esc(STAGE_NAME[a.stage] || a.stage || '—')}`],
    ['QR code', hasQr(a)
      ? '<span class="tag ok">ready</span>'
      : `<span class="tag warn">${esc(qrNote(a))}</span>`],
    printed && ['Already printed',
      `<span class="tag ok">${esc(store.localStamp(printed.at).slice(0, 16))}</span>`
      + (printed.times > 1 ? ` <span class="tag">${printed.times}x</span>` : '')],
  ].filter(Boolean);
  $('#pmDetails').innerHTML = rows.map(([k, v]) =>
    `<dt>${k}</dt><dd>${v}</dd>`).join('');
}

/* The two links a guest is sent: straight into their ticket page, and the RSVP
   form. Both are shown in full so they can be checked before being pasted, and
   both can become a QR for handing over on a phone screen. */
function renderLinks(a) {
  const basis = rsvpBasis(a);
  const rows = [
    { id: 'magic', name: 'Magic link — my ticket', url: magicLink(a.ticket),
      note: 'signs the guest straight in' },
    { id: 'rsvp', name: 'RSVP form', url: rsvpLink(a),
      note: basis.kind === 'none'
        ? 'no phone or email on this ticket — nothing to address the form to'
        : `by ${basis.kind}: ${basis.value}` },
  ];
  $('#pmLinks').innerHTML = '<h4>Guest links</h4>' + rows.map(r => `
    <div class="linkrow ${r.url ? '' : 'none'}" data-link="${r.id}">
      <div class="top">
        <span class="name">${esc(r.name)}</span>
        <span class="acts">
          ${r.url ? `
            <button class="sm" data-copy="${r.id}">Copy</button>
            <button class="sm" data-qr="${r.id}">QR</button>
            <button class="sm ghost" data-open="${r.id}">Open</button>` : ''}
        </span>
      </div>
      <div class="hint" style="margin-top:4px">${esc(r.note)}</div>
      ${r.url ? `<div class="url">${esc(r.url)}</div>` : ''}
    </div>`).join('');

  const urlOf = id => rows.find(r => r.id === id).url;

  $$('#pmLinks [data-copy]').forEach(b => b.onclick = async () => {
    const url = urlOf(b.dataset.copy);
    try {
      await navigator.clipboard.writeText(url);
      toast('Link copied');
    } catch {
      // Clipboard access is refused in some contexts; select it instead so the
      // operator can still copy by hand rather than being told nothing.
      const el = b.closest('.linkrow').querySelector('.url');
      const range = document.createRange();
      range.selectNodeContents(el);
      const sel = getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
      toast('Selected — press Ctrl/Cmd+C', 4000);
    }
  });

  $$('#pmLinks [data-open]').forEach(b => b.onclick =
    () => window.open(urlOf(b.dataset.open), '_blank', 'noopener'));

  $$('#pmLinks [data-qr]').forEach(b => b.onclick = async () => {
    const row = b.closest('.linkrow');
    const existing = row.querySelector('canvas');
    if (existing) { existing.remove(); b.textContent = 'QR'; return; }
    b.textContent = 'Hide QR';
    const img = new Image();
    img.src = await qrPngDataUrl(urlOf(b.dataset.qr));
    await img.decode();
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 220;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = false;      // keep the modules hard-edged
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    row.append(canvas);
  });
}

function openPrintModal(guest, kind = 'label') {
  M.guest = guest;
  M.kind = kind;
  $('#pmTitle').textContent = guest.name;
  $('#pmWho').textContent =
    `${guest.company || '—'} · ${BY_SLUG[guest.category].label}`;
  renderDetails(guest);
  renderLinks(guest);
  $('#pmW').value = P.labelWidthMm;
  $('#pmH').value = P.labelHeightMm;
  showSizes();
  setKind(kind);
  renderModalFields();
  $('#printModal').classList.remove('hide');
  refreshModalPreview();
}

function closePrintModal() {
  $('#printModal').classList.add('hide');
  M.guest = null;
}

function setKind(kind) {
  M.kind = kind;
  $$('#pmKind button').forEach(b =>
    b.setAttribute('aria-pressed', b.dataset.kind === kind));
  $('#pmSize').classList.toggle('hide', kind !== 'label');
}

function renderModalFields() {
  $('#pmFields').innerHTML = DETAIL_FIELDS.map(f => `
    <button data-field="${f.key}" aria-pressed="${P.fields.includes(f.key)}">
      ${f.label}</button>`).join('');
  $$('#pmFields button').forEach(b => b.onclick = () => {
    b.setAttribute('aria-pressed', b.getAttribute('aria-pressed') !== 'true');
    P.fields = DETAIL_FIELDS.map(f => f.key).filter(k =>
      $(`#pmFields [data-field="${k}"]`).getAttribute('aria-pressed') === 'true');
    store.savePrefs({ fields: P.fields });
    renderFieldPicker();          // the batch screen shares this choice
    refreshModalPreview();
    refreshPreview();
  });
}

async function buildFor(guest, kind) {
  const one = await withQr(guest);
  return kind === 'card'
    ? renderCards([one], { pageMode: 'single', fields: P.fields, cardHeightMm: P.cardHeightMm })
    : renderLabels([one], { ...labelOpts(),
        widthMm: Number($('#pmW').value) || P.labelWidthMm,
        heightMm: Number($('#pmH').value) || P.labelHeightMm });
}

async function refreshModalPreview() {
  if (!M.guest) return;
  const mine = ++M.token;
  const notes = [];
  if (!hasQr(M.guest)) notes.push(qrNote(M.guest) + ' — it prints without one');
  if (M.guest.category === 'family' && !M.guest.parentName) {
    notes.push(M.guest.parent
      ? `host ticket ${M.guest.parent} is not in the loaded list`
      : 'family badge with no parent ticket on it');
  }
  $('#pmWarn').innerHTML = notes.length
    ? `<span class="tag warn">${esc(notes.join(' · '))}</span>` : '';
  try {
    const bytes = await buildFor(M.guest, M.kind);
    if (mine !== M.token) return;
    M.bytes = bytes;
    await renderPdfToCanvas(bytes, $('#pmCanvas'), { maxWidth: 560 });
  } catch (err) {
    $('#pmWarn').innerHTML = `<span class="tag bad">${esc(err.message)}</span>`;
  }
}

$$('#pmKind button').forEach(b => b.onclick = () => {
  setKind(b.dataset.kind);
  refreshModalPreview();
});
['#pmW', '#pmH'].forEach(sel => $(sel).addEventListener('change', () => {
  P.labelWidthMm = Number($('#pmW').value) || P.labelWidthMm;
  P.labelHeightMm = Number($('#pmH').value) || P.labelHeightMm;
  store.savePrefs({ labelWidthMm: P.labelWidthMm, labelHeightMm: P.labelHeightMm });
  $('#labelW').value = P.labelWidthMm;
  $('#labelH').value = P.labelHeightMm;
  $('#deskLabelW').value = P.labelWidthMm;
  $('#deskLabelH').value = P.labelHeightMm;
  refreshModalPreview();
}));
$('#pmClose').onclick = closePrintModal;
$('#pmCancel').onclick = closePrintModal;
$('#printModal').addEventListener('click', e => {
  if (e.target.id === 'printModal') closePrintModal();
});
addEventListener('keydown', e => {
  if (e.key === 'Escape' && !$('#printModal').classList.contains('hide')) {
    closePrintModal();
  }
});
$('#pmSave').onclick = () => {
  if (!M.bytes) return;
  downloadPdf(M.bytes, `${M.kind}_${M.guest.ticket}.pdf`);
};
$('#pmPrint').onclick = async () => {
  const guest = M.guest;
  const kind = M.kind;
  const bytes = M.bytes;
  closePrintModal();
  try {
    await printPdf(bytes, {
      ...(kind === 'card' ? cardSizeMm(P.cardHeightMm)
        : { widthMm: Number(P.labelWidthMm), heightMm: Number(P.labelHeightMm) }),
      title: `${guest.name} — ${BY_SLUG[guest.category].label}`,
    });
    toast(`${guest.name} — sent to the printer`);
    recordPrint(guest, kind);
    refreshFeed();
    runSearch();
  } catch (err) {
    toast(err.message, 5000);
  }
};

/* ============================== scan desk ============================ */


/* An unconfirmed guest has no payload to encode; the badge just prints with the
   QR area blank rather than failing. */
async function withQr(a) {
  return { ...a, qrPng: a.qr ? await qrPngDataUrl(a.qr) : null };
}

async function buildLabel(a) {
  return renderLabels([await withQr(a)], labelOpts());
}

async function buildCard(a) {
  return renderCards([await withQr(a)], { pageMode: 'single', fields: P.fields, cardHeightMm: P.cardHeightMm });
}

async function issueLabel(a, { print = true, download = false } = {}) {
  const bytes = await buildLabel(a);
  S.lastLabel = bytes;
  S.lastGuest = a;
  if (download) {
    downloadPdf(bytes, `label_${a.ticket}.pdf`);
    return bytes;
  }
  if (print) {
    await printPdf(bytes, {
      widthMm: P.labelWidthMm, heightMm: P.labelHeightMm,
      title: `${a.name} — ${BY_SLUG[a.category].label}`,
    });
  }
  return bytes;
}


/** A scan is a qr_string, a ticket number, or something with one inside it. */
async function resolve(code) {
  const raw = (code || '').trim();
  if (!raw) return null;
  const byTicket = t => S.attendees.find(a =>
    a.ticket && a.ticket.toUpperCase() === String(t).trim().toUpperCase());

  const live = S.attendees.find(a => a.qr && a.qr === raw);
  if (live) return { a: live, how: 'qr_string' };

  // Payloads from earlier loads: the API re-encrypts on every read, so the
  // string printed on a card is not the one in the current roster.
  const remembered = S.payloads[raw];
  if (remembered && byTicket(remembered)) {
    return { a: byTicket(remembered), how: 'a QR we printed earlier' };
  }

  const direct = byTicket(raw);
  if (direct) return { a: direct, how: 'ticket number' };
  for (const token of raw.split(/[|:;,\s/#]+/)) {
    const hit = byTicket(token);
    if (hit) return { a: hit, how: 'embedded ticket' };
  }

  // With the key, the nonce no longer matters: decrypt and read the ticket
  // number straight out of the payload. This is the path that makes a guest's
  // own e-ticket scan, rather than only badges this device printed.
  if ((S.serverDecrypt || S.qrKey) && looksLikeTicketQr(raw)) {
    try {
      const user = S.serverDecrypt
        ? await serverDecrypt(raw)
        : await decodeQr(raw, S.qrKey);
      const hit = byTicket(user.ticketNumber);
      if (hit) return { a: hit, how: 'decrypted QR' };
      // The payload carries company, name, position and ticket number, so a
      // guest missing from the roster is still fully identified — only their
      // category is unknown, and that decides the artwork.
      return { decoded: user };
    } catch (err) {
      return { error: err.message };
    }
  }

  // Last resort: a resolve endpoint, if the team has given us one.
  if (api.getResolveUrl()) {
    try {
      const ticket = await api.resolveCode(raw);
      const hit = byTicket(ticket);
      if (hit) return { a: hit, how: 'resolve endpoint' };
    } catch (err) {
      return { error: err.message };
    }
  }
  return null;
}


/* The desk is one loop: identify a guest, print their label, next. Everything
   here serves that — a single result panel that is readable across a table, a
   sound so the operator can look at the guest instead of the screen, and an
   automatic return to idle so the queue never waits for a click. */

const deskEl = $('#deskState');
let idleTimer;

/* ---- sound ---------------------------------------------------------- */

let audio;
function beep(kind) {
  if (!P.sound) return;
  try {
    audio = audio || new (window.AudioContext || window.webkitAudioContext)();
    if (audio.state === 'suspended') audio.resume();
    const tones = { ok: [880, 0.09], dup: [560, 0.16], bad: [200, 0.3] }[kind];
    const osc = audio.createOscillator();
    const gain = audio.createGain();
    osc.frequency.value = tones[0];
    osc.type = kind === 'bad' ? 'sawtooth' : 'sine';
    gain.gain.setValueAtTime(0.14, audio.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, audio.currentTime + tones[1]);
    osc.connect(gain).connect(audio.destination);
    osc.start();
    osc.stop(audio.currentTime + tones[1]);
  } catch { /* no audio on this device; the colour still says it */ }
}

function paintAutoPrint() {
  $('#autoPrint').setAttribute('aria-pressed', String(P.autoPrint));
  $('#autoPrintLabel').textContent =
    P.autoPrint ? 'Auto-print on' : 'Auto-print off';
  $('#autoPrintHint').textContent = P.autoPrint
    ? 'a label prints the moment a guest is found'
    : 'scans only look the guest up — you press Print';
}

$('#autoPrint').onclick = () => {
  P.autoPrint = !P.autoPrint;
  store.savePrefs({ autoPrint: P.autoPrint });
  paintAutoPrint();
};

$('#soundToggle').onclick = () => {
  P.sound = !P.sound;
  store.savePrefs({ sound: P.sound });
  $('#soundToggle').textContent = P.sound ? 'sound on' : 'sound off';
  $('#soundToggle').setAttribute('aria-pressed', String(P.sound));
  if (P.sound) beep('ok');
};

/* ---- result states -------------------------------------------------- */

function deskRender(state, html, actions = '') {
  clearTimeout(idleTimer);
  deskEl.className = `result is-${state}`;
  deskEl.innerHTML = `<div class="rbody">${html}</div>` +
    (actions ? `<div class="ract">${actions}</div>` : '');
}

function deskIdle(message = 'Ready — scan a QR, or type a name.') {
  deskRender('idle', `<div>${esc(message)}</div>`);
  S.pendingDup = null;
  focusDesk();
}

const deskBusy = () => deskRender('busy', '<div>Reading…</div>');

/* Hand back to idle on its own, so the next guest can just scan. */
function idleAfter(seconds) {
  clearTimeout(idleTimer);
  idleTimer = setTimeout(() => deskIdle(), seconds * 1000);
}

function guestBlock(a, categoryLabel, extraMeta = '') {
  return `
    <div class="rwho">
      <div class="rstatus"><span class="dotbig"></span><span id="rlabel"></span></div>
      <h2>${esc(a.name)}</h2>
      <p class="rsub">${esc(a.company) || '—'}${
        a.pillar && a.pillar !== a.company ? ' · ' + esc(a.pillar) : ''}</p>
      <div class="rmeta">
        <span class="catbadge" style="--c:var(--${a.category})">${categoryLabel}</span>
        <span class="tag mono">${esc(a.ticket)}</span>
        ${extraMeta}
      </div>
    </div>
    <canvas id="deskCanvas"></canvas>`;
}

const setStatus = text => { const el = $('#rlabel'); if (el) el.textContent = text; };

/* ---- the scan itself ------------------------------------------------ */

async function doScan(code, force = false) {
  if (!code.trim()) return;
  S.lastScannedCode = code.trim();
  clearResults();
  deskBusy();

  const found = await resolve(code);
  if (found?.decoded) return deskDecoded(found.decoded);
  if (!found || found.error) {
    return deskFail(code, found?.error || (S.attendees.length
      ? 'That code is not in the loaded guest list.'
      : 'No guests loaded — open Guests and load them first.'));
  }

  const a = found.a;
  if (!printable(a)) return deskFail(code, issues(a).join(', '));

  const previous = store.printedTickets().get(a.ticket);
  if (previous && P.warnOnReprint && !force) {
    S.pendingDup = a.ticket;
    beep('dup');
    deskRender('dup', guestBlock(a, BY_SLUG[a.category].label,
      `<span class="tag warn">already printed ${
        esc(store.localClock(previous.at))}</span>`),
      `<button class="primary" id="printAnyway">Print again</button>
       <button class="ghost" id="cancelDup">Skip — next guest</button>`);
    setStatus('Already printed');
    renderDeskLabel(a);
    $('#printAnyway').onclick = () => doScan(a.ticket, true);
    $('#cancelDup').onclick = () => deskIdle('Skipped.');
    return;
  }

  await issueAndShow(a, found.how);
}

async function issueAndShow(a, how) {
  const shouldPrint = P.autoPrint;
  let bytes = null;
  let error = '';
  try {
    bytes = await issueLabel(a, { print: shouldPrint });
  } catch (err) {
    error = err.message;
  }

  if (shouldPrint && !error) recordPrint(a, how);

  const meta = `<span class="tag">matched on ${esc(how)}</span>` +
    (hasQr(a) ? '' : `<span class="tag warn">${esc(qrNote(a))}</span>`);

  if (error) {
    beep('bad');
    deskRender('bad', guestBlock(a, BY_SLUG[a.category].label, meta),
      `<button class="primary" id="printAgain">Try printing again</button>
       <span class="hint">${esc(error)}</span>`);
    setStatus('Print failed');
  } else if (shouldPrint) {
    beep('ok');
    deskRender('ok', guestBlock(a, BY_SLUG[a.category].label, meta),
      `<button id="printAgain">Print again</button>
       <button class="ghost" id="openOptions">Change what's on it…</button>
       <span class="spacer"></span>
       <span class="hint">ready for the next guest</span>`);
    setStatus('Printed');
    idleAfter(8);
  } else {
    beep('ok');
    deskRender('ok', guestBlock(a, BY_SLUG[a.category].label, meta),
      `<button class="primary" id="printAgain">Print label</button>
       <button class="ghost" id="openOptions">Change what's on it…</button>`);
    setStatus('Found — not printed yet');
  }

  if (bytes) renderPdfToCanvas(bytes, $('#deskCanvas'), { maxWidth: 420 });
  else renderDeskLabel(a);

  const again = $('#printAgain');
  if (again) again.onclick = async () => {
    again.disabled = true;
    try {
      await issueLabel(a, { print: true });
      recordPrint(a, 'reprint');
      toast(`${a.name} — sent to the printer`);
      refreshFeed();
      runSearch();
    } catch (err) { toast(err.message, 5000); }
    again.disabled = false;
  };
  const opts = $('#openOptions');
  if (opts) opts.onclick = () => openPrintModal(a, 'label');
  refreshFeed();
  runSearch();
  focusDesk();
}

/* Show the label even when nothing was printed, so the operator can see what
   they are about to hand over. */
async function renderDeskLabel(a) {
  const canvas = $('#deskCanvas');
  if (!canvas) return;
  try { await renderPdfToCanvas(await buildLabel(a), canvas, { maxWidth: 420 }); }
  catch { /* the identity is what matters here, not the picture */ }
}

/* A QR that decrypted but whose ticket is not in the roster — someone
   registered after the list was loaded, or filtered out of it. Everything the
   badge needs is in the payload except the category, so ask for that one thing
   rather than turning the guest away. */
function deskDecoded(user) {
  beep('dup');
  deskRender('dup', `
    <div class="rwho">
      <div class="rstatus"><span class="dotbig"></span><span id="rlabel"></span></div>
      <h2>${esc(user.name) || '(no name in the QR)'}</h2>
      <p class="rsub">${esc(user.company) || '—'}${
        user.position ? ' · ' + esc(user.position) : ''}</p>
      <div class="rmeta">
        <span class="tag mono">${esc(user.ticketNumber)}</span>
        <span class="tag">read from the QR</span></div>
    </div>`,
    `<span class="hint">Not in the guest list — pick a category to print:</span>
     <div class="cats" id="walkupCats" style="margin-top:0">${PRINT_ORDER.map(slug => `
       <button class="cat" data-slug="${slug}" style="--c:var(--${slug})"
         ><i></i>${BY_SLUG[slug].label}</button>`).join('')}</div>`);
  setStatus('Valid ticket, unknown category');
  $$('#walkupCats .cat').forEach(b => b.onclick = () => {
    openPrintModal({
      ticket: user.ticketNumber, name: user.name, company: user.company,
      position: user.position, pillar: '', parent: '', parentName: '',
      category: b.dataset.slug, qr: S.lastScannedCode || '', stage: '',
    }, 'label');
  });
}

function deskFail(code, error) {
  const raw = (code || '').trim();
  const looksEncrypted = /^v\d+\.[0-9a-f]{8,}\./i.test(raw);
  beep('bad');
  deskRender('bad', `
    <div class="rwho">
      <div class="rstatus"><span class="dotbig"></span><span id="rlabel"></span></div>
      <h2>Not found</h2>
      <p class="rsub">${esc(error)}</p>
      ${looksEncrypted && !S.qrKey && !S.serverDecrypt ? `<p class="hint"
        style="margin-top:10px">That is a valid ticket QR, but not one this
        device printed, and no decryption secret is set — see Desk settings.</p>` : ''}
      <div class="rmeta"><span class="tag mono">${esc(raw.slice(0, 44))}${
        raw.length > 44 ? '…' : ''}</span></div>
    </div>`,
    `<span class="hint">Type the guest's name in the box above to find them.</span>`);
  setStatus('No match');
  S.pendingDup = null;
  focusDesk();
  idleAfter(12);
}

/* ---- input: one box, and a gun that works wherever focus is --------- */

const looksLikeCode = v =>
  /^v\d+\./i.test(v) || v.length > 24 || /^[A-Z0-9]{10,}$/.test(v.trim());

function focusDesk() {
  const box = $('#deskInput');
  if (box && $('[data-view=scan]')?.classList.contains('on') &&
      $('#printModal').classList.contains('hide')) {
    box.focus();
  }
}

let deskTimer;
function handleEntry(value) {
  const text = value.trim();
  if (!text) return;
  clearTimeout(deskTimer);
  clearTimeout(burstTimer);
  if (looksLikeCode(text)) {
    $('#deskInput').value = '';
    doScan(text);
    return;
  }
  const hits = searchGuests(text);
  if (hits.length === 1) {
    $('#deskInput').value = '';
    clearResults();
    pickGuest(hits[0]);
  } else {
    showResults(hits, text);
  }
}

/* Scanners disagree about what ends a scan. Enter is the common default, Tab is
   the next most common, and plenty ship configured to send nothing at all. So
   any of the three works: Enter, Tab, or simply the keystrokes stopping. */
const TERMINATORS = ['Enter', 'Tab'];

/* A person cannot type a 200-character ticket payload, so the burst detector can
   afford to be generous about inter-character timing. Bluetooth scanners in
   particular pace themselves, and an earlier 45 ms limit was tight enough to
   drop a scan whenever one character arrived late. */
const GUN_MAX_GAP_MS = 120;
const BURST_END_MS = 160;

let burstTimer;

$('#deskInput').addEventListener('keydown', e => {
  if (scannerTest.active) return;
  if (TERMINATORS.includes(e.key)) {
    e.preventDefault();               // Tab must not move focus mid-scan
    handleEntry(e.target.value);
  }
});

$('#deskInput').addEventListener('input', e => {
  const value = e.target.value.trim();
  clearTimeout(deskTimer);
  clearTimeout(burstTimer);

  if (looksLikeCode(value)) {
    clearResults();
    // No terminator configured? Submit once the characters stop arriving.
    burstTimer = setTimeout(() => handleEntry($('#deskInput').value), BURST_END_MS);
    return;
  }
  deskTimer = setTimeout(() => {
    if (value.length < 2) return clearResults();
    showResults(searchGuests(value), value);
  }, 180);
});

/* A scanner gun is a keyboard that types faster than any person. Watching for
   that at the document level means it still works when focus has wandered onto
   a button, which on a busy desk it always eventually does. */
const gun = { buffer: '', last: 0, timer: null };

function gunSubmit() {
  const code = gun.buffer;
  gun.buffer = '';
  clearTimeout(gun.timer);
  if (code.length >= 8 && looksLikeCode(code)) doScan(code);
}

document.addEventListener('keydown', e => {
  if (scannerTest.active) return;
  if (!$('[data-view=scan]')?.classList.contains('on')) return;
  if (!$('#printModal').classList.contains('hide')) return;
  if (e.target === $('#deskInput')) return;          // the box handles itself
  // e.target can be the document itself, which has no matches(); and a guest
  // is never harmed by a stray keystroke, but a dropped scan costs the queue.
  if (e.target?.matches?.('input, textarea, select')) return;
  if (e.metaKey || e.ctrlKey || e.altKey) return;

  const now = Date.now();
  if (now - gun.last > GUN_MAX_GAP_MS) gun.buffer = '';
  gun.last = now;
  clearTimeout(gun.timer);

  if (TERMINATORS.includes(e.key)) {
    e.preventDefault();
    gunSubmit();
    return;
  }
  if (e.key.length === 1) {
    gun.buffer += e.key;
    gun.timer = setTimeout(gunSubmit, BURST_END_MS);
  }
});

/* Clicking anywhere quiet puts the caret back, so the next scan lands. */
document.addEventListener('click', e => {
  if (!$('[data-view=scan]')?.classList.contains('on')) return;
  if (!$('#printModal').classList.contains('hide')) return;
  if (e.target.closest('button,input,select,a,textarea,summary')) return;
  focusDesk();
});

/* ---- scanner test ---------------------------------------------------
 *
 * "The scanner is connected but nothing happens" has several causes that look
 * identical from the outside: it may be paired as a serial device rather than a
 * keyboard, it may send a terminator the page is not watching for, or it may be
 * truncating a long payload. This records the raw keystrokes and says which. */

const scannerTest = { active: false, keys: [], last: 0, timer: null };

function scannerTestReport() {
  const keys = scannerTest.keys;
  if (!keys.length) {
    $('#scannerOut').innerHTML =
      `<p class="hint">Nothing arrived. If the scanner is connected but no keys
       reach the browser, it is almost certainly not in keyboard (HID) mode —
       many models default to a serial or "virtual COM" mode that no web page
       can read. Its manual will have a setup barcode to switch to
       <b>USB HID Keyboard</b>.</p>`;
    return;
  }
  const chars = keys.filter(k => k.key.length === 1);
  const text = chars.map(k => k.key).join('');
  const gaps = chars.slice(1).map((k, i) => k.at - chars[i].at);
  const maxGap = gaps.length ? Math.max(...gaps) : 0;
  const avgGap = gaps.length
    ? Math.round(gaps.reduce((a, b) => a + b, 0) / gaps.length) : 0;
  const last = keys[keys.length - 1].key;
  const terminator = last.length > 1 ? last : '(none — keystrokes just stopped)';
  const isTicket = looksLikeTicketQr(text);

  const notes = [];
  if (!TERMINATORS.includes(last) && last.length > 1) {
    notes.push(`The scanner ends with <b>${esc(last)}</b>, which the desk does
      not treat as an end-of-scan. Configure it to send Enter, or nothing.`);
  }
  if (maxGap > GUN_MAX_GAP_MS) {
    notes.push(`Slowest gap between characters was <b>${maxGap} ms</b>, above the
      ${GUN_MAX_GAP_MS} ms the desk allows when focus is outside the box. Inside
      the box it still works; a lower "inter-character delay" on the scanner
      would make it reliable everywhere.`);
  }
  if (text.length && !isTicket) {
    notes.push(`What arrived is not a ticket QR. Either this was a different
      barcode, or characters were lost or altered — check the scanner's keyboard
      layout is set to US.`);
  }

  $('#scannerOut').innerHTML = `
    <table class="tbl" style="margin-top:10px">
      <tr><th>characters</th><td>${chars.length}</td></tr>
      <tr><th>terminator</th><td>${esc(terminator)}</td></tr>
      <tr><th>timing</th><td>avg ${avgGap} ms, slowest ${maxGap} ms</td></tr>
      <tr><th>looks like a ticket QR</th>
          <td>${isTicket ? '<span class="tag ok">yes</span>'
                         : '<span class="tag warn">no</span>'}</td></tr>
    </table>
    <div class="url" style="margin-top:10px">${esc(text.slice(0, 240))}${
      text.length > 240 ? '…' : ''}</div>
    ${notes.map(n => `<p class="hint" style="margin-top:10px">${n}</p>`).join('')}`;
}

/* The most useful verdict is the one where nothing arrives at all, so waiting
   for a keystroke to trigger the report would never reach it. Give the scan a
   few seconds, then report whatever did or did not turn up. */
const SCANNER_TEST_WAIT_MS = 8000;

$('#apiUsage').addEventListener('toggle', () => {
  if (!$('#apiUsage').open) return;
  const log = api.callLog();
  const days = Object.keys(log).sort().reverse();
  const total = d => Object.values(log[d]).reduce((a, b) => a + b, 0);
  $('#apiUsageOut').innerHTML = days.length ? `
    <table class="tbl"><tr><th>day</th><th>calls</th><th>what</th></tr>
    ${days.map(d => `<tr><td>${esc(d)}</td><td class="mono">${total(d)}</td>
      <td class="hint">${esc(Object.entries(log[d])
        .map(([k, n]) => `${n} ${k}`).join(' · '))}</td></tr>`).join('')}</table>
    <p class="hint" style="margin-top:10px">Today: <b>${api.callsToday()}</b>.
      A sign-in lasts five hours and the whole guest list arrives in one call,
      so a desk costs a handful a day.</p>`
    : '<p class="hint">No calls recorded on this device yet.</p>';
});

$('#scannerTest').addEventListener('toggle', () => {
  scannerTest.active = $('#scannerTest').open;
  scannerTest.keys = [];
  clearTimeout(scannerTest.timer);
  if (scannerTest.active) {
    $('#scannerOut').innerHTML =
      '<p class="hint">Listening — scan a badge now.</p>';
    scannerTest.timer = setTimeout(scannerTestReport, SCANNER_TEST_WAIT_MS);
  } else {
    focusDesk();
  }
});

document.addEventListener('keydown', e => {
  if (!scannerTest.active) return;
  if (e.key === 'Escape') return;
  e.preventDefault();
  scannerTest.keys.push({ key: e.key, at: Date.now() });
  clearTimeout(scannerTest.timer);
  scannerTest.timer = setTimeout(scannerTestReport, 400);
}, true);

function searchGuests(query) {
  const q = query.toLowerCase();
  return S.attendees.filter(a => printable(a) && haystack(a).includes(q))
    .slice(0, 8);
}

const clearResults = () => { $('#deskResults').innerHTML = ''; };

function showResults(hits, query) {
  if (!hits.length) {
    $('#deskResults').innerHTML =
      `<p class="hint" style="padding:10px 6px">No guest matches
       “${esc(query)}”.</p>`;
    return;
  }
  $('#deskResults').innerHTML = hits.map(a => `
    <button data-pick="${esc(a.ticket)}"><b>${esc(a.name)}</b>
      <span>${esc(a.company) || '—'}</span>
      <span class="tag" style="flex:none;background:color-mix(in srgb,
        var(--${a.category}) 13%,#fff);color:var(--${a.category}-ink)"
        >${BY_SLUG[a.category].label}</span></button>`).join('');
  $$('#deskResults [data-pick]').forEach(b => b.onclick = () => {
    $('#deskInput').value = '';
    clearResults();
    pickGuest(S.attendees.find(x => x.ticket === b.dataset.pick));
  });
}

/** A guest chosen by name goes through the same issue-and-log path as a scan. */
function pickGuest(guest) { doScan(guest.ticket, false); }

/* ------------------------- QR decryption ---------------------------- */


/* Two ways to read a scanned QR. If the deployment carries the secret in its
   environment, a Pages Function does the decryption and the browser never sees
   the secret. Otherwise the operator pastes it in and the browser decrypts
   locally, which is also what keeps the desk working with the network down. */
async function serverDecryptAvailable() {
  try {
    const r = await fetch('/api/resolve');
    if (!r.ok) return false;
    return !!(await r.json()).available;
  } catch {
    return false;              // no Functions here — running from a plain server
  }
}

async function serverDecrypt(code) {
  const r = await fetch('/api/resolve', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json',
               Authorization: `Bearer ${api.getToken()}` },
    body: JSON.stringify({ code }),
  });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(body.error || `resolve failed (${r.status})`);
  return body.user;
}

/* ------------------------- QR decryption key ------------------------- */

function setKeyStatus(state, note) {
  const tag = $('#keyTag');
  tag.className = 'tag ' + ({ ok: 'ok', bad: 'bad', off: '' }[state] || '');
  tag.textContent = { ok: 'working', bad: 'not working', off: 'not set' }[state];
  $('#keyNote').innerHTML = note || '';
}

/* Proving the key works is easy and worth doing: decrypt a QR we already know
   the answer to, and check it comes back with the right ticket number. */
async function testKey(text) {
  const key = await importKey(text);
  const sample = S.attendees.find(a => a.qr && a.ticket);
  if (!sample) throw new Error('load the guest list first, then test the key');
  const user = await decodeQr(sample.qr, key);
  if (user.ticketNumber !== sample.ticket) {
    throw new Error(`decrypted, but it says ${user.ticketNumber} where the ` +
                    `roster says ${sample.ticket}`);
  }
  return { key, user, sample };
}

async function useStoredKey() {
  const text = store.qrKey();
  if (!text) return setKeyStatus('off');
  try {
    const { key, sample } = await testKey(text);
    S.qrKey = key;
    $('#qrKey').value = text;
    setKeyStatus('ok', `verified against ${esc(sample.name)} — scanning reads ` +
                       `any SMDD ticket QR, not just badges printed here`);
  } catch (err) {
    S.qrKey = null;
    setKeyStatus('bad', `<span style="color:var(--bad)">${esc(err.message)}</span>`);
  }
}

$('#qrKeySave').onclick = async () => {
  const text = $('#qrKey').value.trim();
  if (!text) return setKeyStatus('off', 'nothing entered');
  setKeyStatus('off', 'checking…');
  try {
    const { key, user, sample } = await testKey(text);
    S.qrKey = key;
    store.saveQrKey(text);
    setKeyStatus('ok', `verified — decrypted ${esc(sample.name)}'s QR to ` +
                       `<span class="mono">${esc(user.ticketNumber)}</span>`);
    toast('QR secret saved and verified');
  } catch (err) {
    S.qrKey = null;
    setKeyStatus('bad', `<span style="color:var(--bad)">${esc(err.message)}</span>`);
  }
};

$('#qrKeyClear').onclick = () => {
  store.clearQrKey();
  S.qrKey = null;
  $('#qrKey').value = '';
  setKeyStatus('off', 'forgotten');
};

/* ------------------------------ camera ------------------------------ */

function stopCamera() {
  S.camera?.stop();
  const btn = $('#camToggle');
  if (btn) btn.textContent = 'Camera';
  $('#cameraMode')?.classList.add('hide');
}

$('#camToggle').onclick = async () => {
  if (S.camera?.running) {
    stopCamera();
    return;
  }
  S.camera = S.camera || new CameraScanner($('#camVideo'), {
    onCode: code => doScan(code),
  });
  $('#cameraMode').classList.remove('hide');
  $('#camNote').textContent = 'starting…';
  try {
    await S.camera.start();
    $('#camToggle').textContent = 'Stop camera';
    $('#camNote').textContent =
      'Hold the QR in the frame. Only badges printed from this device will match.';
  } catch (err) {
    $('#camNote').innerHTML = `<span style="color:var(--bad)">${esc(err.message)}</span>`;
    $('#cameraMode').classList.add('hide');
  }
};

/* The log holds one row per badge sent to the printer -- a reprint is a second
   row, not a correction of the first. Nothing else is recorded, so every row
   here reads the same way. */
function recordPrint(a, note) {
  store.appendLog({ ticket: a.ticket, name: a.name, company: a.company,
    category: a.category, printed: true, note });
}

function refreshFeed() {
  const stats = store.logStats();
  $('#statPrinted').textContent = stats.printed;
  $('#statUnique').textContent = stats.unique;
  const rows = store.printEvents().slice(-60).reverse();
  $('#feed').innerHTML = rows.length ? rows.map(r => `<li>
    <span class="t">${store.localClock(r.at)}</span>
    <span class="n">${esc(r.name)}</span>
    <span class="tag ok">printed</span>
    </li>`).join('')
    : '<li class="hint" style="padding:18px 16px">Nothing printed yet.</li>';
}

/* What came off the printer, and nothing else. */
$('#exportLog').onclick = () => {
  const { printed, unique } = store.logStats();
  if (!printed) return toast('Nothing printed on this device yet.', 4000);
  const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, '');
  const url = URL.createObjectURL(
    new Blob([store.printedCsv(S.attendees)], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `smdd2026_printed_${stamp}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 20000);
  toast(`Downloaded — ${printed} badge${printed === 1 ? '' : 's'} `
      + `for ${unique} guest${unique === 1 ? '' : 's'}`, 4000);
};
$('#resetLog').onclick = () => {
  if (!confirm('Clear this device’s print log? Every guest goes back to '
             + '“not printed”. The guest list itself stays.')) return;
  store.clearLog();
  refreshFeed();
  runSearch();
  toast('Print log cleared');
};

/* ------------------------ characters the font lacks --------------------

   A guest whose name carries Chinese, Japanese or Korean, or an emoji, has
   characters the badge font has no letters for. They are left off the card
   rather than printed as boxes -- but that has to be said, before printing,
   and not discovered on a finished badge. */

const PRINTED_FIELDS = ['name', 'company', 'position', 'pillar', 'parentName'];

async function checkGlyphs() {
  await preload([]);                       // the font, which decides it
  S.glyphless = [];
  let changed = false;
  for (const a of S.attendees) {
    const lost = new Set(PRINTED_FIELDS.flatMap(k => undrawable(a[k])));
    const blank = !!a.name && !asPrinted(a.name);
    if (!!a.nameUndrawable !== blank) { a.nameUndrawable = blank; changed = true; }
    if (lost.size) S.glyphless.push(a);
  }
  renderGlyphWarn();
  if (changed) renderGuests();             // a blank name changes what prints
  else refreshPreview();
}

function renderGlyphWarn() {
  const el = $('#glyphWarn');
  const list = S.glyphless;
  el.classList.toggle('hide', !list.length);
  if (!list.length) return;
  const blank = list.filter(a => a.nameUndrawable);
  const row = a => {
    const field = PRINTED_FIELDS.find(k => undrawable(a[k]).length);
    const before = a[field];
    const after = asPrinted(before);
    return `<li><span class="codes">${esc(a.ticket)}</span> ·
      ${esc(before)} → <b>${after ? esc(after) : '(nothing left)'}</b>${
      field === 'name' ? '' : ` <span class="hint">(${esc(field)})</span>`}</li>`;
  };
  el.innerHTML = `<b>${list.length} guest${list.length === 1 ? ' has' : 's have'}
    characters the badge font has no letters for</b> — Chinese, Japanese,
    Korean or emoji. Those characters are left off the badge rather than
    printed as empty boxes.
    ${blank.length ? `<br><b>${blank.length}</b> of them ${blank.length === 1
      ? 'has a name' : 'have names'} written only in those characters, so
      nothing would be left to print: ${blank.length === 1 ? 'it is' : 'they are'}
      held back until a Latin name is added to the ticket.` : ''}
    <details style="margin-top:8px"><summary>Show what prints</summary>
    <ul style="margin:8px 0 0;padding-left:18px">${list.map(row).join('')}</ul>
    </details>`;
}

/* --------------------------- blank templates ---------------------------

   The artwork alone, at the size cards are printed now. No guest list is
   needed, so it works before one has been loaded -- which is when blank stock
   tends to be ordered. */

function renderBlankChips() {
  $('#blankCats').innerHTML = PRINT_ORDER.map(c => `
    <button class="cat" data-blank="${c}" aria-pressed="false"
      style="--c:var(--${c})"><i></i>${BY_SLUG[c].label}</button>`).join('');
  $$('#blankCats .cat').forEach(b => b.onclick = () => {
    b.setAttribute('aria-pressed', b.getAttribute('aria-pressed') !== 'true');
    blankNote();
  });
  blankNote();
}

const chosenBlanks = () => {
  const picked = $$('#blankCats .cat[aria-pressed=true]').map(b => b.dataset.blank);
  return picked.length ? picked : [...PRINT_ORDER];
};

function blankNote() {
  const n = chosenBlanks().length;
  const { widthMm, heightMm } = cardSizeMm(P.cardHeightMm);
  $('#blankBtn').textContent = n === PRINT_ORDER.length
    ? 'Download all blank templates' : `Download ${n} blank template${n === 1 ? '' : 's'}`;
  $('#blankNote').textContent =
    `${mm(widthMm)} × ${mm(heightMm)} mm — a separate file for each category.`;
}

$('#blankBtn').onclick = async () => {
  const slugs = chosenBlanks();
  const btn = $('#blankBtn');
  btn.disabled = true;
  $('#blankFiles').innerHTML = '';
  try {
    await preload(slugs);
    /* Always one file per category, named for it: blank stock is ordered and
       sent to the printer category by category, and a combined file would have
       to be split apart again by hand. */
    const links = [];
    for (const slug of slugs) {
      const label = BY_SLUG[slug].label;
      const bytes = await renderBlanks([slug], { cardHeightMm: P.cardHeightMm });
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      S.blobUrls.push(url);
      const a = document.createElement('a');
      a.href = url;
      a.download = `SMDD2026 blank - ${label}.pdf`;
      a.innerHTML = `<span><b>${esc(label)}</b> · ${esc(a.download)}</span>
        <span>${(bytes.length / 1e6).toFixed(1)} MB</span>`;
      $('#blankFiles').append(a);
      links.push(a);
    }
    /* Save them all from the one click. Browsers ask once before letting a
       page save several files; the list stays, so anything that was held back
       can still be clicked by hand. */
    for (const a of links) {
      a.click();
      await new Promise(r => setTimeout(r, 400));
    }
  } catch (err) {
    toast(err.message, 5000);
  } finally {
    btn.disabled = false;
  }
};

renderBlankChips();
$('#cardH').addEventListener('change', blankNote);

/* ================================ boot =============================== */

async function start() {
  $('#login').classList.add('hide');
  $('#app').classList.remove('hide');

  // A roster cached by an older build, or for another SKU, is discarded rather
  // than shown — it is the difference between this event and last year's.
  const cached = await store.loadRoster(api.SKU);
  if (cached?.attendees?.length) {
    S.attendees = linkParents(cached.attendees.filter(a => !a.sku || a.sku === api.SKU));
    S.loadedAt = cached.loadedAt;
    S.status = cached.status ?? P.status;
    renderGuests();
    preload();
    checkGlyphs();
  } else {
    /* Nothing usable cached — either the first visit or a build whose rows
       would now be counted differently. Fetch rather than sit on an empty
       screen: the alternative is a desk that looks loaded and is not. */
    loadGuests();
  }
  S.payloads = await store.rememberPayloads(S.attendees);
  $$('.cards-only').forEach(e => e.classList.remove('hide'));
  $$('.labels-only').forEach(e => e.classList.add('hide'));
  renderFieldPicker();
  if (!CameraScanner.supported) {
    const cam = $('#camToggle');
    cam.disabled = true;
    cam.title = window.isSecureContext
      ? 'This browser has no camera API.'
      : 'The camera needs https — open the deployed site.';
  }
  refreshFeed();
  $('#soundToggle').textContent = P.sound ? 'sound on' : 'sound off';
  $('#soundToggle').setAttribute('aria-pressed', String(P.sound));
  paintAutoPrint();
  deskIdle();
  S.serverDecrypt = await serverDecryptAvailable();
  if (S.serverDecrypt) {
    $('#keyBox').classList.add('hide');
    $('#deskMode').textContent =
      'QR decryption runs on the server — no secret needed on this device.';
  } else {
    $('#deskMode').textContent = '';
    useStoredKey();
  }
  watchSession();
  go(location.hash.slice(1) || 'guests');
  if (!S.attendees.length) loadGuests();
  checkConnection();
}

async function boot() {
  if (api.sessionValid()) return start();
  // An expired session with a roster on this device opens the desk offline
  // instead of the sign-in screen. Sign-in is offered from the status line.
  const cached = await store.loadRoster(api.SKU);
  if (cached?.attendees?.length && api.getToken()) return start();
  if (api.getToken()) {
    api.clearToken();
    $('#loginError').textContent = 'Your session expired — sign in again.';
  }
  $('#email').value = api.getEmail();
  ($('#email').value ? $('#password') : $('#email')).focus();
}

boot();
